import { useState, useEffect, useMemo, useRef } from "react";
import { Ban } from "lucide-react";
import usePersistentPageState from "../hooks/usePersistentPageState";
import { useNavigate, useLocation } from "react-router-dom";
import {
  AtlasFilterPanel,
  AtlasFilterChipBar,
  useFilterPanel,
  PersonnelFilterFields,
  buildPersonnelFilterChips,
  countPersonnelActiveFilters,
} from "../components/filters";
import DateInput from "../components/DateInput";
import capitalizeFilterLabel from "../components/filters/utils/capitalizeFilterLabel";
import "react-phone-number-input/style.css";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import { isValidEmail } from "../utils/email";
import { showError, showSuccess, showConfirm } from "../utils/dialog";
import usePageMeta from "../hooks/usePageMeta";
import { useDeleteKeyEasterEgg } from "../hooks/useDeleteKeyEasterEgg";
import Layout from "./Layout";
import { BASE_URL } from "../config";
import "../Styles/Personnel.css";
import ActionDropdown from "../components/ActionDropdown";
import AclRolePopover from "../components/acl/AclRolePopover";
import PersonnelDetailModal from "../components/PersonnelDetailModal";
import { AtlasListToolbar } from "../components/list-toolbar";
import {
  AtlasPageHeader,
  AtlasPageDivider,
  AtlasTabBar,
} from "../components/page-layout";
import AddressAutocomplete from "../components/AddressAutocomplete";
import {
  normalizeAddressValueForCreate,
  applyAddressToForm,
} from "../utils/addressFormUtils";
import { searchWithWildcard } from "../utils/wildcardSearch";
import {
  matchesPersonnelSearch,
  sortPersonnelAlphabetically,
} from "../utils/personnelSearchUtils";
import { useAppPermissions } from "../context/PermissionContext";
import { resolveBackendUrl } from "../utils/accountAvatarUrl";
import defaultProfilePic from "../assets/default_profile_pic.jpeg";
import { formatAccountDateTime } from "../utils/accountDateTime";
import { findDefaultPayerId, payerForTypeChange, usesDefaultPayer } from "../utils/defaultTempPayer";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from "../utils/staffDocumentValidation";

// Simple token management helpers
const getAuthToken = () => {
  return sessionStorage.getItem("authToken") || sessionStorage.getItem("token") || localStorage.getItem("authToken") || localStorage.getItem("token");
};

const getAuthHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

async function requestReindexFace(accountId) {
  const res = await fetch(`${BASE_URL}/api/face/reindex/${accountId}`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok || !j.success) {
    throw new Error(j.message || j.reason || "Re-index failed");
  }
  return j;
}

const TEMP_STAFF_FACE_REINDEX_CURSOR_KEY = "personnel_temp_staff_face_reindex_after_id";

function readStoredReindexCursor() {
  try {
    const s = sessionStorage.getItem(TEMP_STAFF_FACE_REINDEX_CURSOR_KEY);
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeStoredReindexCursor(afterAccountId) {
  try {
    if (afterAccountId > 0) {
      sessionStorage.setItem(TEMP_STAFF_FACE_REINDEX_CURSOR_KEY, String(afterAccountId));
    } else {
      sessionStorage.removeItem(TEMP_STAFF_FACE_REINDEX_CURSOR_KEY);
    }
  } catch (_) {
    // ignore quota / private mode
  }
}

const PERSONNEL_GEOLOCATION_REINDEX_CURSOR_KEY =
  "personnel_geolocation_reindex_after_id";

function readStoredGeolocationReindexCursor() {
  try {
    const s = sessionStorage.getItem(PERSONNEL_GEOLOCATION_REINDEX_CURSOR_KEY);
    const n = parseInt(s, 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0;
  }
}

function writeStoredGeolocationReindexCursor(afterAccountId) {
  try {
    if (afterAccountId > 0) {
      sessionStorage.setItem(
        PERSONNEL_GEOLOCATION_REINDEX_CURSOR_KEY,
        String(afterAccountId),
      );
    } else {
      sessionStorage.removeItem(PERSONNEL_GEOLOCATION_REINDEX_CURSOR_KEY);
    }
  } catch (_) {
    // ignore
  }
}

function getPersonnelRecordId(record) {
  if (!record || typeof record !== "object") return null;
  return record.id || record.account_id || record.accountId || null;
}

async function requestReindexTempStaffBatch(afterAccountId = 0) {
  const res = await fetch(`${BASE_URL}/api/face/reindex-temp-staff`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ after_account_id: afterAccountId }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok || !j.success) {
    throw new Error(j.message || j.reason || "Batch re-index failed");
  }
  return j;
}

async function requestReindexGeolocationBatch(afterAccountId = 0) {
  const res = await fetch(`${BASE_URL}/api/personnel/reindex-geolocation`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json", ...getAuthHeaders() },
    body: JSON.stringify({ after_account_id: afterAccountId }),
  });
  const j = await res.json().catch(() => ({}));
  if (!res.ok || !j.success) {
    throw new Error(j.message || j.reason || "Fix geolocation failed");
  }
  return j;
}

// Render roles compactly: show up to 2 inline badges and collapse the rest behind a "+N more" toggle
function RolesCell({ roles, aclRoles }) {
  const [expanded, setExpanded] = useState(false);

  const structuredRoles = Array.isArray(aclRoles)
    ? aclRoles.filter((r) => r && r.name)
    : [];

  let fallbackLabels = [];
  if (!structuredRoles.length) {
    if (Array.isArray(roles)) {
      fallbackLabels = roles.map((r) =>
        typeof r === "string" ? r : r.name || r.label || String(r),
      );
    } else if (typeof roles === "string") {
      fallbackLabels = roles
        .split(/[,|;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
    } else if (roles != null) {
      fallbackLabels = [String(roles)];
    }
  }

  const totalCount = structuredRoles.length || fallbackLabels.length;
  if (!totalCount) {
    return <span className="text-muted-foreground">—</span>;
  }

  const maxVisible = 2;
  const visibleStructured = expanded
    ? structuredRoles
    : structuredRoles.slice(0, maxVisible);
  const visibleFallback = expanded
    ? fallbackLabels
    : fallbackLabels.slice(0, maxVisible);

  return (
    <div className="flex items-center flex-wrap gap-2 max-w-[28ch] md:max-w-[40ch]">
      {structuredRoles.length > 0
        ? visibleStructured.map((role) => (
            <AclRolePopover key={`${role.roleId}-${role.objectName}`} role={role} />
          ))
        : visibleFallback.map((label, i) => (
            <span
              key={i}
              className="text-sm bg-gray-100 text-gray-800 px-1 py-0.5 rounded truncate max-w-full"
              title={label}
            >
              {label}
            </span>
          ))}
      {!expanded && totalCount > maxVisible && (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="text-sm text-sidebar-active underline ml-1"
          aria-label={`Show ${totalCount - maxVisible} more roles`}
        >
          +{totalCount - maxVisible} more
        </button>
      )}
      {expanded && totalCount > maxVisible && (
        <button
          type="button"
          onClick={() => setExpanded(false)}
          className="text-sm text-muted-foreground underline ml-1"
          aria-label="Show fewer roles"
        >
          show less
        </button>
      )}
    </div>
  );
}

// Default filter state per tab - keeps each tab's filters isolated
function defaultPersonnelTabState() {
  return {
    searchTerm: "",
    currentPage: 1,
    filters: {
      types: [],        
      divisions: [],
      certifications: [],
      skills: [],
      payers: [],
      roles: [],
      recruiters: [],
      hst: "all",       
      deliveryMethod: "all", 
      status: [],       
      showDeleted: false,
      sortBy: "first_name",
      sortOrder: "asc",
    },
  };
}

const PERSONNEL_TABS = ["TEMP STAFF", "OFFICE STAFF", "CHANGE REQUESTS"];

const Personnel = () => {
  usePageMeta("Personnel");
  const perms = useAppPermissions();
  const showTempTab = perms.canReadTempStaff;
  const showOfficeTab = perms.canReadOfficeStaff;
  const mainTabs = useMemo(() => {
    const t = [];
    if (showTempTab) t.push("TEMP STAFF");
    if (showOfficeTab) t.push("OFFICE STAFF");
    return t;
  }, [showTempTab, showOfficeTab]);

  const [selectedTab, setSelectedTab] = useState("TEMP STAFF");

  const [tabQueryState, setTabQueryState] = usePersistentPageState(
    "page_filters_personnel",
    () => Object.fromEntries(PERSONNEL_TABS.map((t) => [t, defaultPersonnelTabState()]))
  );

  // Current tab's slice
  const q = tabQueryState[selectedTab] ?? defaultPersonnelTabState();
  const { searchTerm, currentPage, filters } = q;

  // Update only the current tab's slice
  const patchQuery = (partial) => {
    setTabQueryState((prev) => ({
      ...prev,
      [selectedTab]: { ...prev[selectedTab], ...partial },
    }));
  };

  // Convenience: update only the filters object inside the current tab
  const patchFilters = (updater) => {
    setTabQueryState((prev) => {
      const cur = prev[selectedTab] ?? defaultPersonnelTabState();
      const nextFilters =
        typeof updater === "function" ? updater(cur.filters) : { ...cur.filters, ...updater };
      return { ...prev, [selectedTab]: { ...cur, filters: nextFilters, currentPage: 1 } };
    });
  };
  

  const [personnelData, setPersonnelData] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [copied, setCopied] = useState(false);
  const [tempStaffBulkReindexLoading, setTempStaffBulkReindexLoading] = useState(false);
  const [tempStaffReindexAfterId, setTempStaffReindexAfterId] = useState(readStoredReindexCursor);
  const [geolocationBulkLoading, setGeolocationBulkLoading] = useState(false);
  const [geolocationReindexAfterId, setGeolocationReindexAfterId] = useState(
    readStoredGeolocationReindexCursor,
  );
  const { showEasterEgg: showAdminEasterEgg } = useDeleteKeyEasterEgg();
  const [showCreateTemp, setShowCreateTemp] = useState(false);
  const [showCreateOffice, setShowCreateOffice] = useState(false);
  const [divisions, setDivisions] = useState([]);
  const [recruiters, setRecruiters] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [showRequestDetail, setShowRequestDetail] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [requestStatusFilter, setRequestStatusFilter] = useState("pending");
  const [requestActionLoadingId, setRequestActionLoadingId] = useState(null);
  const filterPanel = useFilterPanel();
  const [filteredPersonnel, setFilteredPersonnel] = useState([]);

  const [certifications, setCertifications] = useState([]);
  const [skills, setSkills] = useState([]);
  const [payers, setPayers] = useState([]);
  const prependCreatedPerson = (created) => {
    if (!created) return;
    const createdId = getPersonnelRecordId(created);
    setPersonnelData((prev) => {
      if (!createdId) {
        return [created, ...prev];
      }
      const withoutDuplicate = prev.filter(
        (item) => getPersonnelRecordId(item) !== createdId
      );
      return [created, ...withoutDuplicate];
    });
  };

  const handlePersonUpdate = (updatedPerson) => {
    if (!updatedPerson) return;
    const targetId = Number(
      updatedPerson.id || updatedPerson.account_id || updatedPerson.accountId
    );
    if (!targetId) return;

    setPersonnelData((prev) =>
      prev.map((p) => {
        const pId = Number(p.id || p.account_id || p.accountId);
        if (pId === targetId) {
          const firstName = updatedPerson.first_name ?? p.first_name;
          const lastName = updatedPerson.last_name ?? p.last_name;
          const name =
            updatedPerson.name ||
            [firstName, lastName].filter(Boolean).join(" ").trim() ||
            p.name;
          const avatarUrl =
            updatedPerson.avatar_url ||
            updatedPerson.profile_picture ||
            p.avatar_url;
          const profilePicture =
            updatedPerson.profile_picture ||
            updatedPerson.avatar_url ||
            p.profile_picture;

          return {
            ...p,
            ...updatedPerson,
            name,
            first_name: firstName,
            last_name: lastName,
            avatar_url: avatarUrl,
            profile_picture: profilePicture,
          };
        }
        return p;
      })
    );

    setSelectedPerson((prev) => {
      if (!prev) return prev;
      const prevId = Number(prev.id || prev.account_id || prev.accountId);
      if (prevId === targetId) {
        const firstName = updatedPerson.first_name ?? prev.first_name;
        const lastName = updatedPerson.last_name ?? prev.last_name;
        const name =
          updatedPerson.name ||
          [firstName, lastName].filter(Boolean).join(" ").trim() ||
          prev.name;
        const avatarUrl =
          updatedPerson.avatar_url ||
          updatedPerson.profile_picture ||
          prev.avatar_url;
        const profilePicture =
          updatedPerson.profile_picture ||
          updatedPerson.avatar_url ||
          prev.profile_picture;

        return {
          ...prev,
          ...updatedPerson,
          name,
          first_name: firstName,
          last_name: lastName,
          avatar_url: avatarUrl,
          profile_picture: profilePicture,
        };
      }
      return prev;
    });
  };

  const [roles, setRoles] = useState([]);
  const [reloadKey, setReloadKey] = useState(0);
  const [initialDetailTab, setInitialDetailTab] = useState("profile");
  const searchFetchSeqRef = useRef(0);

  const navigate = useNavigate();
  const location = useLocation();
  const isChangeRequestsTab = selectedTab === "CHANGE REQUESTS";

  const canWriteCurrentTab = useMemo(() => {
    if (selectedTab === "TEMP STAFF") return perms.canWriteTempStaff;
    if (selectedTab === "OFFICE STAFF") return perms.canWriteOfficeStaff;
    return false;
  }, [selectedTab, perms.canWriteTempStaff, perms.canWriteOfficeStaff]);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openAccountId = params.get("openAccountId");
    const openTab = params.get("openTab") || "profile";
    if (!openAccountId) return;

    const loadAndOpen = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/personnel/accounts/${openAccountId}`, {
          headers: { ...getAuthHeaders() },
          credentials: "include",
        });
        const j = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(j?.message || "Failed to load staff account");

        const person = j?.data || j?.account || j;
        if (!person) throw new Error("Staff account not found");

        const personId = person.id || person.account_id || Number(openAccountId);
        const displayName =
          person.name ||
          `${person.first_name || ""} ${person.last_name || ""}`.trim() ||
          person.email ||
          `#${personId}`;

        setSelectedPerson({
          ...person,
          id: personId,
          name: displayName,
        });
        setInitialDetailTab(openTab);
        setShowDetail(true);
      } catch (err) {
        console.error("Failed to open personnel detail from deep link:", err);
        showError(err.message || "Failed to open staff detail");
      } finally {
        navigate("/personnel", { replace: true });
      }
    };

    loadAndOpen();
  }, [location.search, navigate]);

  useEffect(() => {
    if (selectedTab !== "TEMP STAFF") {
      setTempStaffReindexAfterId(0);
      writeStoredReindexCursor(0);
    }
  }, [selectedTab]);

  // Helper function to format type display
  const formatType = (type) => {
    switch (type) {
      case 'fieldstaff':
        return 'Field Staff';
      case 'candidate':
        return 'Candidate';
      case 'outsourcedstaff':
        return 'Outsourced Staff';
      default:
        return capitalizeFilterLabel(type);
    }
  };

  const formatPersonnelStatus = (status) => {
    const s = String(status || "").trim().toLowerCase();
    if (s === "active") return "Active";
    if (s === "pending_activation" || s === "pending activation") {
      return "Pending Activation";
    }
    if (s === "deleted") return "Deleted";
    return capitalizeFilterLabel(status) || "—";
  };

  const personnelStatusForApi = (statusValues) =>
    (statusValues || []).map((v) => {
      const t = String(v || "").trim().toLowerCase();
      if (t === "pending activation" || t === "pending") return "pending_activation";
      return v;
    });

  // Function to manually refresh personnel data
  const fetchPersonnel = () => {
    patchQuery({ currentPage: 1 });
    setReloadKey((prev) => prev + 1);
  };

  useEffect(() => {
    const refreshOnFocus = () => {
      setReloadKey((prev) => prev + 1);
    };

    const refreshOnVisible = () => {
      if (document.visibilityState === "visible") {
        setReloadKey((prev) => prev + 1);
      }
    };

    window.addEventListener("focus", refreshOnFocus);
    document.addEventListener("visibilitychange", refreshOnVisible);

    return () => {
      window.removeEventListener("focus", refreshOnFocus);
      document.removeEventListener("visibilitychange", refreshOnVisible);
    };
  }, []);

  const loadChangeRequestDetail = async (requestId) => {
    const res = await fetch(`${BASE_URL}/api/temp-staff/requests/${requestId}`, {
      headers: {
        ...getAuthHeaders(),
      },
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || !data?.success) {
      throw new Error(data?.message || "Failed to load request details");
    }
    return data.data;
  };

  const openChangeRequestDetail = async (request) => {
    try {
      const requestId = request?.request_id;
      if (!requestId) return;
      const detail = await loadChangeRequestDetail(requestId);
      setSelectedRequest(detail);
      setShowRequestDetail(true);
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to load request details");
    }
  };

  const approveChangeRequest = async (requestId) => {
    try {
      const ok = await showConfirm("Approve this request and apply changes now?");
      if (!ok) return;
      setRequestActionLoadingId(requestId);
      const res = await fetch(
        `${BASE_URL}/api/temp-staff/requests/${requestId}/approve`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(),
          },
          body: JSON.stringify({ review_note: "" }),
        }
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to approve request");
      }
      showSuccess("Request approved and applied");
      if (showRequestDetail && selectedRequest?.request_id === requestId) {
        const detail = await loadChangeRequestDetail(requestId);
        setSelectedRequest(detail);
      }
      setReloadKey((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to approve request");
    } finally {
      setRequestActionLoadingId(null);
    }
  };

  const rejectChangeRequest = async (requestId) => {
    try {
      const note = window.prompt("Enter rejection reason (optional):", "") || "";
      const ok = await showConfirm("Reject this request?");
      if (!ok) return;
      setRequestActionLoadingId(requestId);
      const res = await fetch(`${BASE_URL}/api/temp-staff/requests/${requestId}/reject`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(),
        },
        body: JSON.stringify({ review_note: note }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data?.success) {
        throw new Error(data?.message || "Failed to reject request");
      }
      showSuccess("Request rejected");
      if (showRequestDetail && selectedRequest?.request_id === requestId) {
        const detail = await loadChangeRequestDetail(requestId);
        setSelectedRequest(detail);
      }
      setReloadKey((prev) => prev + 1);
    } catch (err) {
      console.error(err);
      showError(err.message || "Failed to reject request");
    } finally {
      setRequestActionLoadingId(null);
    }
  };

  // Deterministic avatar color based on name
  const getAvatarColorClass = (name) => {
    const palette = [
      "bg-neutral-900 text-white", // near black
      "bg-neutral-700 text-white", // dark gray
      "bg-neutral-500 text-white", // medium gray
      "bg-neutral-400 text-black", // soft gray
      "bg-neutral-300 text-black", // light gray
      "bg-red-600 text-white", // bold red
      "bg-red-500 text-white", // softer red
      "bg-red-400 text-black", // muted red
      "bg-red-300 text-black", // pastel red
    ];
    const str = (name || "").toString();
    if (!str) return "bg-slate-600";
    let hash = 0;
    for (let i = 0; i < str.length; i++)
      hash = (hash * 31 + str.charCodeAt(i)) >>> 0;
    return palette[hash % palette.length];
  };

  const handleSearch = (val) => {
    patchQuery({ searchTerm: val, currentPage: 1 });
  };

  useEffect(() => {
    setFilteredPersonnel(personnelData);
  }, [personnelData]);

  //!load data
  const loadSkills = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/skills`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.data || []);
      setSkills(list.map((s) => s.name || s));
    } catch (err) {
      console.error("Failed to load skills", err);
      setSkills([]);
    }
  };

  const loadCertifications = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/certifications`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.data || []);
      setCertifications(list.map((c) => c.name || c));
    } catch (err) {
      console.error("Failed to load certifications", err);
      setCertifications([]);
    }
  };

  const loadPayers = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/payers`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.data || []);
      setPayers(list.map((p) => p.name || p));
    } catch (err) {
      console.error("Failed to load payers", err);
      setPayers([]);
    }
  };

  const loadDivisions = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/divisions`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.data || []);
      setDivisions(list);
    } catch (err) {
      console.error("Failed to load divisions", err);
      setDivisions([]);
    }
  };

  const loadRecruiters = async () => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/recruiters`);
      const json = await res.json();
      const list = Array.isArray(json) ? json : (json.data || []);
      setRecruiters(list);
    } catch (err) {
      console.error("Failed to load recruiters", err);
      setRecruiters([]);
    }
  };

  useEffect(() => {
    if (!filterPanel.isOpen) return;
    if (selectedTab === "TEMP STAFF") {
      loadDivisions();
      loadSkills();
      loadCertifications();
      loadPayers();
      loadRecruiters();
    }
    if (selectedTab === "OFFICE STAFF") {
      loadRoles();
    }
    
  }, [filterPanel.isOpen, selectedTab]);

  useEffect(() => {
    if (!filterPanel.isOpen) {
      document.body.style.overflow = "";
      return undefined;
    }

    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    if (isMobile) {
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [filterPanel.isOpen]);

  const loadRoles = async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/roles?level=app`
      );
      const json = await res.json();
      const list = Array.isArray(json) ? json : json.data || [];
      const appOnly = Array.isArray(list)
        ? list.filter((r) => {
            const level = String(
              r?.level ??
                r?.role_level ??
                r?.context ??
                r?.roleLevel ??
                "app",
            )
              .trim()
              .toLowerCase();
            return level === "app";
          })
        : [];
      setRoles(appOnly);
    } catch (err) {
      console.error("Failed to load roles", err);
      setRoles([]);
    }
  };

  // Filter chip helpers
  const removeFilter = (key, value = null) => {
    if (key === 'search') {
      patchQuery({ searchTerm: "", currentPage: 1 });
      return;
    }
    patchFilters((prev) => {
      const next = { ...prev };
      if (Array.isArray(next[key])) {
        next[key] = value !== null ? next[key].filter((v) => v !== value) : [];
      } else if (typeof next[key] === "boolean") {
        next[key] = false;
      } else {
        next[key] = "all";
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    // Reset only the current tab
    setTabQueryState((prev) => ({
      ...prev,
      [selectedTab]: defaultPersonnelTabState(),
    }));
  };

  const handleTabChange = (tab) => {
    if (tab === selectedTab) return;
    setSelectedTab(tab);
  };

  useEffect(() => {
    if (!perms.loaded) return;
    if (!mainTabs.length) return;
    if (!mainTabs.includes(selectedTab)) {
      setSelectedTab(mainTabs[0]);
    }
  }, [perms.loaded, mainTabs, selectedTab]);

  const copyToClipboard = async () => {
    try {
      const url = `${window.location.origin}/signup`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 3000); // hide message after 3s
    } catch (err) {
      console.error("Failed to copy!", err);
    }
  };

  const handleBulkReindexTempStaffFaces = async () => {
    const continuing = tempStaffReindexAfterId > 0;
    const confirmMsg = continuing
      ? `Continue batch face re-index after account ID ${tempStaffReindexAfterId}? Up to 500 per run. May take several minutes (AWS).`
      : "Re-index faces for all temp staff (up to 500 per run)? May take several minutes and uses AWS Rekognition/S3.";
    if (!window.confirm(confirmMsg)) return;
    setTempStaffBulkReindexLoading(true);
    try {
      const j = await requestReindexTempStaffBatch(tempStaffReindexAfterId);
      let msg = `Batch complete: ${j.succeeded} succeeded, ${j.failed} failed (${j.processed} processed).`;
      if (j.truncated && j.nextAfterAccountId != null) {
        setTempStaffReindexAfterId(j.nextAfterAccountId);
        writeStoredReindexCursor(j.nextAfterAccountId);
        msg += ` About ${j.remainingEstimate} remaining — click again to continue.`;
      } else {
        setTempStaffReindexAfterId(0);
        writeStoredReindexCursor(0);
      }
      showSuccess(msg, continuing ? "Re-index faces (continue)" : "Re-index all temp faces");
      if (j.errorsTruncated) {
        showError("Additional per-account errors were omitted from the response.", "Batch re-index");
      }
    } catch (err) {
      console.error(err);
      showError(err.message || "Batch re-index failed", "Re-index all temp faces");
    } finally {
      setTempStaffBulkReindexLoading(false);
    }
  };

  const geolocationReindexHasCursor = geolocationReindexAfterId > 0;

  const handleBulkReindexGeolocation = async () => {
    const continuing = geolocationReindexAfterId > 0;
    const confirmMsg = continuing
      ? `Continue fixing staff geolocation after account ID ${geolocationReindexAfterId}? Up to 10,000 per run.`
      : "Fix geolocation for all staff with missing coordinates (up to 10,000 per run)? Uses OpenStreetMap geocoding.";
    if (!window.confirm(confirmMsg)) return;
    setGeolocationBulkLoading(true);
    try {
      const j = await requestReindexGeolocationBatch(geolocationReindexAfterId);
      let msg = `Geolocation batch: ${j.succeeded} succeeded, ${j.failed} failed (${j.processed} processed).`;
      if (j.truncated && j.nextAfterAccountId != null) {
        setGeolocationReindexAfterId(j.nextAfterAccountId);
        writeStoredGeolocationReindexCursor(j.nextAfterAccountId);
        msg += ` About ${j.remainingEstimate} remaining — click again to continue.`;
      } else {
        setGeolocationReindexAfterId(0);
        writeStoredGeolocationReindexCursor(0);
      }
      showSuccess(msg, continuing ? "Fix geolocation (continue)" : "Fix geolocation");
      if (j.errorsTruncated) {
        showError(
          "Additional per-account errors were omitted from the response.",
          "Fix geolocation",
        );
      }
    } catch (err) {
      console.error(err);
      showError(err.message || "Fix geolocation failed", "Fix geolocation");
    } finally {
      setGeolocationBulkLoading(false);
    }
  };

  /**
   * BACKEND IMPLEMENTATION REQUIRED:
   *
   * Endpoint: GET /api/personnel/accounts/:id/metrics?period=30
   *
   * RELIABILITY CALCULATION:
   * reliability = (confirmed_attended_shifts) / (total_assigned_shifts) * 100
   *
   * SQL Query:
   * SELECT
   *   (COUNT(CASE WHEN sa.state IN ('confirmed', 'completed') AND sc.checkin_date IS NOT NULL THEN 1 END)
   *    / COUNT(*)) * 100 as reliability,
   *   (SUM(TIMESTAMPDIFF(MINUTE, sc.checkin_date, sc.checkout_date)) / 60) /
   *   (a.max_hours * (period_days / 7)) * 100 as utilization
   * FROM shifts s
   * JOIN shift_accounts sa ON s.id = sa.shift_id
   * LEFT JOIN shift_checkins sc ON sa.id = sc.shift_account_id
   * JOIN accounts a ON sa.account_id = a.id
   * WHERE sa.account_id = ?
   *   AND s.date >= DATE_SUB(NOW(), INTERVAL ? DAY)
   * GROUP BY a.id, a.max_hours
   *
   * UTILIZATION CALCULATION:
   * utilization = (total_hours_worked / available_hours) * 100
   * Where:
   * - total_hours_worked = SUM(TIMESTAMPDIFF(MINUTE, checkin_date, checkout_date) / 60)
   * - available_hours = max_hours * weeks_in_period
   */

  // Function to calculate reliability metrics
  const calculateReliabilityMetrics = async (accountId, period = 30) => {
    //console.log(`🔄 Fetching metrics for account ${accountId}`);
    try {
      const [reliabilityRes, metricsRes] = await Promise.all([
        fetch(`${BASE_URL}/api/personnel/accounts/${accountId}/reliability`, {
          headers: {
            ...getAuthHeaders(),
          },
          credentials: "include",
        }),
        fetch(`${BASE_URL}/api/personnel/accounts/${accountId}/metrics?period=${period}`),
      ]);
      const reliabilityData = await reliabilityRes.json().catch(() => ({}));
      const metricsData = await metricsRes.json().catch(() => ({}));
      // console.log(`📊 UTILIZATION CHECK for ${accountId}:`, {
      //   utilization: metricsData?.data?.utilization,
      //   hours_worked: metricsData?.data?.hours_worked,
      //   max_hours: metricsData?.data?.max_hours,
      //   utilization_percent: metricsData?.data?.utilization_percent,
      // });
      const reliabilityScore =
        reliabilityRes.ok && reliabilityData?.success
          ? reliabilityData?.data?.reliability_score
          : null;
      const metricPayload =
        metricsRes.ok && metricsData?.success ? metricsData.data || {} : {};
      return {
        ...metricPayload,
        reliability:
          typeof reliabilityScore === "number"
            ? reliabilityScore
            : metricPayload.reliability ?? 0,
        completed_shifts:
          reliabilityData?.data?.worked_shift_count ??
          metricPayload.completed_shifts ??
          0,
        total_assigned:
          reliabilityData?.data?.reliability_details?.total_assigned ??
          metricPayload.total_assigned ??
          0,
      };
    } catch (err) {
      console.error("❌ Error calculating metrics:", err);
      return {
        reliability: 0,
        utilization: 0,
        completed_shifts: 0,
        total_assigned: 0,
      };
    }
  };
  //! helper to load recruiters realted to temp
  const fetchRelatedRecruiter = async (tempStaffId) => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/personnel/recruiters/${tempStaffId}`
      );
      if (!res.ok) return null;

      const data = await res.json();
      return Array.isArray(data) && data.length > 0 ? data[0] : null;
    } catch (err) {
      console.error("Failed to fetch recruiter for temp staff", tempStaffId, err);
      return null;
    }
  };

  // Frontend safety net: validate active cert links per account when cert filters are used.
  const fetchActiveCertificationNames = async (accountId) => {
    try {
      const res = await fetch(`${BASE_URL}/api/personnel/accounts/${accountId}/certifications`, {
        headers: {
          ...getAuthHeaders(),
        },
        credentials: "include",
      });
      if (!res.ok) return [];
      const json = await res.json().catch(() => ({}));
      const list = Array.isArray(json?.data) ? json.data : [];
      return list
        .map((row) => String(row?.certification_name || "").trim().toLowerCase())
        .filter(Boolean);
    } catch (err) {
      console.warn("Failed to validate active certifications for account:", accountId, err);
      return [];
    }
  };


  // Fetch personnel data whenever selectedTab, currentPage, searchTerm, filters, or reloadKey changes
  useEffect(() => {
    const fetchData = async () => {
      try {
        const requestSeq = ++searchFetchSeqRef.current;
        const isStaleRequest = () => requestSeq !== searchFetchSeqRef.current;
        if (selectedTab === "CHANGE REQUESTS") {
          const params = new URLSearchParams({
            page: currentPage,
            limit: rowsPerPage,
          });
          if (searchTerm.trim()) params.append("q", searchTerm.trim());
          if (requestStatusFilter !== "all") {
            params.append("status", requestStatusFilter);
          }
          const res = await fetch(`${BASE_URL}/api/temp-staff/requests?${params.toString()}`, {
            headers: {
              ...getAuthHeaders(),
            },
          });
          const data = await res.json().catch(() => ({}));
          if (!res.ok || !data?.success) {
            throw new Error(data?.message || "Failed to fetch change requests");
          }
          const requestRows = Array.isArray(data.data) ? data.data : [];
          if (isStaleRequest()) {
            return;
          }
          setPersonnelData(requestRows);
          setTotalPages(data?.pagination?.totalPages || 1);
          setTotalCount(data?.pagination?.total || requestRows.length || 0);
          return;
        }

        const endpoint = selectedTab === "TEMP STAFF" ? "temp" : "office";
        const trimmedSearch = String(searchTerm || "").trim();
        const numericSearch = /^\d+$/.test(trimmedSearch);

        // Construct query parameters from filters
        const params = new URLSearchParams({
          page: currentPage,
          limit: rowsPerPage,
          search: trimmedSearch,
          sortBy: filters.sortBy,
          sortOrder: filters.sortOrder
        });

        // Backend variants: some routes expect q and/or explicit employee id keys.
        if (trimmedSearch) {
          params.append("q", trimmedSearch);
        }
        if (numericSearch) {
          params.append("employeeId", trimmedSearch);
          params.append("employee_id", trimmedSearch);
          params.append("accountId", trimmedSearch);
          params.append("account_id", trimmedSearch);
          params.append("id", trimmedSearch);
        }

        // Add all filter arrays if they have values
        if (filters.types?.length) params.append('types', filters.types.join(','));
        if (filters.divisions?.length) params.append('divisions', filters.divisions.join(','));
        if (filters.recruiters?.length) params.append('recruiters', filters.recruiters.join(','));
        if (filters.certifications?.length) params.append('certifications', filters.certifications.join(','));
        if (filters.skills?.length) params.append('skills', filters.skills.join(','));
        if (filters.payers?.length) params.append('payers', filters.payers.join(','));
        if (filters.roles?.length) params.append('roles', filters.roles.join(','));
        if (filters.status?.length) {
          params.append("status", personnelStatusForApi(filters.status).join(","));
        }

        if (filters.hst !== 'all') params.append('hst', filters.hst);
        if (filters.deliveryMethod !== 'all') {
          const normalizedDeliveryMethod =
            filters.deliveryMethod === "mail"
              ? "delivery_mail"
              : filters.deliveryMethod === "pickup"
                ? "delivery_pickup"
                : filters.deliveryMethod;
          params.append('deliveryMethod', normalizedDeliveryMethod);
        }
        if (filters.showDeleted) params.append('showDeleted', '1');

        if (selectedTab === "TEMP STAFF") {
          params.append('include_unregistered', '1');
        }

        const res = await fetch(`${BASE_URL}/api/personnel/${endpoint}?${params.toString()}`);
        const data = await res.json();
        let personnel = Array.isArray(data.data) ? data.data : [];

        // Numeric Employee ID fallback: direct account lookup when list search returns no match.
        if (numericSearch && personnel.length === 0) {
          try {
            const byIdRes = await fetch(
              `${BASE_URL}/api/personnel/accounts/${encodeURIComponent(trimmedSearch)}`,
              {
                headers: {
                  ...getAuthHeaders(),
                },
                credentials: "include",
              },
            );

            if (byIdRes.ok) {
              const byIdJson = await byIdRes.json().catch(() => ({}));
              const personRaw = byIdJson?.data || byIdJson?.account || null;
              if (personRaw) {
                const personType = String(personRaw.type || "").toLowerCase();
                const isTempTab = selectedTab === "TEMP STAFF";
                const tabMatches = isTempTab
                  ? ["fieldstaff", "candidate", "outsourcedstaff"].includes(personType)
                  : ["office", "officestaff"].includes(personType);

                if (tabMatches) {
                  const personId =
                    personRaw.id ||
                    personRaw.account_id ||
                    personRaw.accountId ||
                    Number(trimmedSearch);
                  const displayName =
                    personRaw.name ||
                    `${personRaw.first_name || ""} ${personRaw.last_name || ""}`.trim() ||
                    personRaw.email ||
                    `#${personId}`;

                  personnel = [
                    {
                      ...personRaw,
                      id: personId,
                      name: displayName,
                      phone:
                        personRaw.phone ||
                        personRaw.phone_mobile ||
                        personRaw.phone_home ||
                        "",
                      type: personRaw.type || (isTempTab ? "fieldstaff" : "office"),
                      division:
                        personRaw.division ||
                        personRaw.division_name ||
                        personRaw.divisionName ||
                        "",
                      payer:
                        personRaw.payer ||
                        personRaw.payer_name ||
                        personRaw.agency_name ||
                        "",
                      status: personRaw.status || personRaw.state || "",
                      created: personRaw.created || personRaw.created_at || "",
                    },
                  ];
                }
              }
            }
          } catch (byIdErr) {
            console.warn("Personnel ID fallback lookup failed:", byIdErr);
          }
        }
        
        if (
          selectedTab === "TEMP STAFF" &&
          Array.isArray(filters.certifications) &&
          filters.certifications.length > 0 &&
          personnel.length > 0
        ) {
          const requiredCerts = filters.certifications
            .map((c) => String(c || "").trim().toLowerCase())
            .filter(Boolean);

          const validated = await Promise.all(
            personnel.map(async (person) => {
              const accountId = person?.id || person?.account_id || person?.accountId;
              if (!accountId) return null;
              const activeCerts = await fetchActiveCertificationNames(accountId);
              const activeSet = new Set(activeCerts);
              const hasAllRequired = requiredCerts.every((reqName) => activeSet.has(reqName));
              return hasAllRequired ? person : null;
            })
          );

          personnel = validated.filter(Boolean);
        }

        if (trimmedSearch) {
          personnel = personnel.filter((p) => matchesPersonnelSearch(p, trimmedSearch));
          // /temp already returns best name matches first; keep that order.
          if (selectedTab !== "TEMP STAFF") {
            personnel.sort((a, b) =>
              filters.sortOrder === "desc"
                ? sortPersonnelAlphabetically(b, a)
                : sortPersonnelAlphabetically(a, b)
            );
          }
        }

        const personnelWithPlaceholders = personnel.map(person => ({
          ...person,
          reliability: null, // Will load in background
          utilization: null,
          completed_shifts: 0,
          total_assigned: 0,
          hours_worked: 0,
          max_hours: null,
          recruiterId: null,
          recruiterName: person.recruiterName || "—", // Use from backend
          recruiterRole: null,
        }));

        if (isStaleRequest()) {
          return;
        }

        // Set data immediately so table renders fast
        setPersonnelData(personnelWithPlaceholders);
        const hasClientPruned =
          Boolean(trimmedSearch) &&
          personnel.length < (data?.data?.length || 0);
        const resolvedTotal = hasClientPruned
          ? personnel.length
          : data?.total ?? (personnel.length || 0);
        const resolvedTotalPages = hasClientPruned
          ? Math.max(1, Math.ceil(personnel.length / rowsPerPage))
          : data?.totalPages || (resolvedTotal > 0 ? Math.ceil(resolvedTotal / rowsPerPage) : 1);

        setTotalPages(resolvedTotalPages);
        setTotalCount(resolvedTotal);

        
        personnel.forEach(async (person) => {
          try {
            const metrics = await calculateReliabilityMetrics(person.id);

            // Update this person's data in state
            setPersonnelData(prev =>
              prev.map(p =>
                p.id === person.id
                  ? {
                    ...p,
                    reliability: metrics.reliability,
                    utilization: metrics.utilization,
                    completed_shifts: metrics.completed_shifts,
                    total_assigned: metrics.total_assigned,
                    hours_worked: metrics.hours_worked,
                    max_hours: metrics.max_hours,
                  }
                  : p
              )
            );
          } catch (err) {
            console.error(`❌ Failed to load metrics for person ${person.id}:`, err);
          }
        });

      } catch (err) {
        console.error(err);
        setPersonnelData([]);
      }
    };
    fetchData();
  }, [selectedTab, rowsPerPage, tabQueryState, reloadKey, requestStatusFilter]);

  // Reset page to 1 when rowsPerPage changes
  useEffect(() => patchQuery({ currentPage: 1 }), [rowsPerPage]); 

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/clients/divisions`);
        const json = await res.json();
        setDivisions(json.data || []);
      } catch (_) { }
      try {
        const res = await fetch(`${BASE_URL}/api/personnel/office?limit=1000`);
        if (res.ok) {
          const json = await res.json();
          setRecruiters(json.data || []);
          //console.log("Recruiters loaded:", json.data || []);
        }
      } catch (_) { }
    })();
  }, []);


  // Helper to compute a compact page list with ellipses
  const getPageList = (total, current) => {
    const delta = 2; // neighbors to show
    const range = [];
    const rangeWithDots = [];
    let l;

    for (let i = 1; i <= total; i++) {
      if (
        i === 1 ||
        i === total ||
        (i >= current - delta && i <= current + delta)
      ) {
        range.push(i);
      }
    }

    for (const i of range) {
      if (l) {
        if (i - l === 2) {
          rangeWithDots.push(l + 1);
        } else if (i - l > 2) {
          rangeWithDots.push("...");
        }
      }
      rangeWithDots.push(i);
      l = i;
    }

    return rangeWithDots;
  };

  
  const getInitials = (name = "") => {
    const safeName = (name || "").toString();
    const parts = safeName.trim().split(/\s+/);
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return parts[0]?.slice(0, 2).toUpperCase() || "NA";
  };

  // Avatar component - uses staff uploaded profile picture or default profile picture fallback
  const AvatarComponent = ({ person }) => {
    const [imageError, setImageError] = useState(false);
    
    // Use avatar_url directly from the person object (included in initial fetch)
    const avatarUrl = resolveBackendUrl(person?.avatar_url || person?.profile_picture);

    useEffect(() => {
      setImageError(false);
    }, [avatarUrl]);
    
    if (imageError || !avatarUrl || !avatarUrl.trim()) {
      return (
        <div
          className="w-6 h-6 rounded-full flex items-center justify-center shadow-sm overflow-hidden border border-gray-200 bg-white"
          title={person?.name || "Staff"}
          style={{ minWidth: "1.5rem", minHeight: "1.5rem" }}
        >
          <img
            src={defaultProfilePic}
            alt={person?.name || "avatar"}
            className="w-full h-full object-cover"
          />
        </div>
      );
    }

    return (
      <div
        className="w-6 h-6 rounded-full flex items-center justify-center shadow-sm overflow-hidden border border-gray-200 bg-white"
        title={person?.name || "avatar"}
        style={{ minWidth: "1.5rem", minHeight: "1.5rem" }}
      >
        <img
          key={avatarUrl}
          src={avatarUrl}
          alt={person?.name || "avatar"}
          className="w-full h-full object-cover"
          onError={() => {
            setImageError(true);
          }}
        />
      </div>
    );
  };

  const SortArrow = ({ colKey }) => {
    const sortKeys = Array.isArray(colKey) ? colKey : [colKey];
    const isActive = sortKeys.includes(filters.sortBy);
    const defaultSortKey = sortKeys[0];

    return (
      <button
        onClick={() => {
          if (isActive) {
            patchFilters({ sortOrder: filters.sortOrder === "asc" ? "desc" : "asc" });
          } else {
            patchFilters({ sortBy: defaultSortKey, sortOrder: "asc" });
          }
        }}
        className="float-right mr-1 text-sm text-white/90 hover:text-white"
        title="Sort"
      >
        {isActive ? (filters.sortOrder === "asc" ? "▲" : "▼") : "⇅"}
      </button>
    );
  };

  const startRow = totalCount === 0 ? 0 : (currentPage - 1) * rowsPerPage + 1;
  const endRow = (currentPage - 1) * rowsPerPage + personnelData.length;
  const sortLabels = isChangeRequestsTab
    ? {
      created_at: "Requested At",
      status: "Status",
    }
    : {
      first_name: "First Name",
      last_name: "Last Name",
      created: "Created",
      ...(selectedTab !== "OFFICE STAFF" && { type: "Type" }),
    };
  const sortOptions = useMemo(
    () => Object.entries(sortLabels).map(([value, label]) => ({ value, label })),
    [sortLabels]
  );
  const officeRoleOptions = useMemo(() => {
    const seen = new Set();
    return roles.filter((role) => {
      const name = String(role?.name || "").trim();
      if (!name || seen.has(name)) return false;
      seen.add(name);
      return true;
    });
  }, [roles]);

  const filterChips = useMemo(
    () => buildPersonnelFilterChips(filters, searchTerm, formatType),
    [filters, searchTerm], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const activeFilterCount = useMemo(
    () => countPersonnelActiveFilters(filters, searchTerm, formatType),
    [filters, searchTerm], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const tempStaffBulkReindexHasCursor = tempStaffReindexAfterId > 0;

  return (
    <Layout active="Personnel">
      <div className="py-3 px-2 flex flex-col p-6 min-h-screen no-mobile-table-stack">
        <AtlasPageHeader title="Personnel" />
        <AtlasPageDivider />

        <div className="mt-6 mx-6 p-4 border-2 border-gray-200 rounded-xl h-full min-w-0 flex flex-col">
          {/* Tabs */}
          <AtlasTabBar
            tabs={mainTabs}
            activeTab={selectedTab}
            onTabChange={handleTabChange}
          />

          <div className={`min-w-0 max-w-full ${filterPanel.contentOffsetClass}`}>
          <AtlasListToolbar
            searchValue={searchTerm}
            onSearchChange={(val) => patchQuery({ searchTerm: val, currentPage: 1 })}
            showFilter={!isChangeRequestsTab}
            onFilterClick={filterPanel.toggle}
            filterOpen={filterPanel.isOpen}
            onRefresh={() => fetchPersonnel()}
            extraControls={
              isChangeRequestsTab ? (
                <select
                  value={requestStatusFilter}
                  onChange={(e) => {
                    setRequestStatusFilter(e.target.value);
                    patchQuery({ currentPage: 1 });
                  }}
                  className="rounded-md border border-gray-300 bg-white px-2 py-1 text-sm focus:outline-none focus:ring-1 focus:ring-sidebar-active/30"
                >
                  <option value="all">All statuses</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="cancelled">Cancelled</option>
                </select>
              ) : null
            }
            resultsMeta={{
              startRow,
              endRow,
              totalCount,
              pageSize: rowsPerPage,
              onPageSizeChange: (size) => {
                setRowsPerPage(size);
                patchQuery({ currentPage: 1 });
              },
              showSort: !isChangeRequestsTab,
              sortBy: filters.sortBy,
              sortOrder: filters.sortOrder,
              sortOptions,
              onSortByChange: (value) => patchFilters({ sortBy: value }),
              onSortOrderToggle: () =>
                patchFilters({ sortOrder: filters.sortOrder === "asc" ? "desc" : "asc" }),
            }}
            actions={
              !isChangeRequestsTab && canWriteCurrentTab ? (
                <>
                  <button
                    type="button"
                    className="rounded-lg bg-sidebar-active px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-600"
                    onClick={() =>
                      selectedTab === "TEMP STAFF"
                        ? setShowCreateTemp(true)
                        : setShowCreateOffice(true)
                    }
                  >
                    CREATE
                  </button>
                  <button
                    type="button"
                    onClick={copyToClipboard}
                    className="relative rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary"
                  >
                    REGISTRATION FORM
                    {copied && (
                      <span className="absolute -top-6 left-1/2 -translate-x-1/2 rounded bg-green-500 px-2 py-1 text-xs text-white">
                        Copied to clipboard!
                      </span>
                    )}
                  </button>
                  {selectedTab === "TEMP STAFF" && showAdminEasterEgg && (
                    <button
                      type="button"
                      disabled={tempStaffBulkReindexLoading}
                      onClick={handleBulkReindexTempStaffFaces}
                      className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {tempStaffBulkReindexLoading
                        ? "Re-indexing…"
                        : tempStaffBulkReindexHasCursor
                          ? "Re-index faces (continue)"
                          : "Re-index all faces"}
                    </button>
                  )}
                  {(selectedTab === "TEMP STAFF" ||
                    selectedTab === "OFFICE STAFF") &&
                    showAdminEasterEgg && (
                      <button
                        type="button"
                        disabled={geolocationBulkLoading}
                        onClick={handleBulkReindexGeolocation}
                        className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        {geolocationBulkLoading
                          ? "Fixing geolocation…"
                          : geolocationReindexHasCursor
                            ? "Fix geolocation (continue)"
                            : "Fix geolocation"}
                      </button>
                    )}
                </>
              ) : null
            }
            chipBar={
              !isChangeRequestsTab ? (
                <AtlasFilterChipBar
                  chips={filterChips}
                  onChipClick={(chip) => {
                    if (chip.fieldId) filterPanel.focusField(chip.fieldId);
                    else filterPanel.open();
                  }}
                  onRemove={(chip) => removeFilter(chip.key, chip.value)}
                  onClearAll={clearAllFilters}
                />
              ) : null
            }
          />
          {/* Table */}
          <div className="flex-1 min-h-0">
            <div className="atlas-list-table-wrap bg-card rounded-lg border border-border overflow-x-auto h-full table-force-rows">
              {selectedTab === "TEMP STAFF" && (
                <table className="w-full min-w-[1100px] table-fixed border-collapse">
                  <thead className="bg-[#dc1f0080] text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-2 px-1 text-left text-sm font-medium w-[17%] break-words-all">
                        Name 
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium whitespace-nowrap w-[10%]">
                        Phone
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium break-words w-[9%]">
                        Type <SortArrow colKey="type" />
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium break-words w-[10%]">
                        Division
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium break-words w-[15%]">
                        Payer / Outsourced Agency
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium break-words w-[8%]">
                        Reliability
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium break-words w-[8%]">
                        Utilization
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium break-words w-[10%]">
                        Status
                      </th>
                      <th className="py-2 px-1 text-left text-sm font-medium w-[10%]">
                        Created <SortArrow colKey="created" />
                      </th>
                      {canWriteCurrentTab ? (
                        <th className="py-2 px-1 w-[3%]"></th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersonnel.map((person) => (
                      <tr
                        key={person.id}
                        className="border-t border-border hover:bg-table-row-hover align-top"
                      >
                        <td
                          className={`py-2 px-1 ${person.status?.toLowerCase() === 'deleted' ? '' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (person.status?.toLowerCase() === 'deleted') return;
                            setSelectedPerson(person);
                            setShowDetail(true);
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <AvatarComponent person={person} />
                            <div className="flex flex-col min-w-0">
                              <div className="flex items-center gap-1.5">
                                <span
                                  className={`font-medium whitespace-normal break-words break-all text-sm ${person.status?.toLowerCase() === 'deleted' ? 'text-gray-500' : 'text-red-600'}`}
                                  style={{ wordBreak: "break-word" }}
                                >
                                  {person.name || "—"}
                                </span>
                                {Boolean(person.global_do_not_place) && (
                                  <div className="relative group/dnp inline-flex items-center">
                                    <Ban className="w-4 h-4 text-red-600 shrink-0 cursor-pointer" title="Do Not Place" />
                                    <span className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 hidden group-hover/dnp:block whitespace-nowrap rounded bg-neutral-900 px-2 py-0.5 text-xs font-normal text-white shadow-md z-30">
                                      Do Not Place
                                    </span>
                                  </div>
                                )}
                              </div>
                              {person.id && (
                                <span className="text-xs text-muted-foreground">
                                  {person.id}
                                </span>
                              )}
                            </div>
                          </div>
                        </td>

                        <td className="py-2 px-1 text-muted-foreground text-sm break-words">
                          {person.phone}
                        </td>
                        <td className="py-2 px-1 break-words text-sm">
                          {`${formatType(person.type)}`}
                          {person.recruiterName && person.recruiterName !== "—" ? <br /> : ''}
                          {person.recruiterName && person.recruiterName !== "—" ? <span className="text-xs text-muted-foreground">Recruiter name: {person.recruiterName}</span> : ''}
                        </td>
                        <td className="py-2 px-1 break-words text-sm">
                          <div>{person.division}</div>
                          {person.subDivision && (
                            <div className="text-sm text-muted-foreground break-words">
                              {person.subDivision}
                            </div>
                          )}
                          {person.location && (
                            <div className="text-sm text-muted-foreground break-words">
                              {person.location}
                            </div>
                          )}
                        </td>
                        <td className="py-2 px-1 break-words text-sm">
                          {person.payer}
                        </td>
                        <td className="py-2 px-1 break-words text-sm">
                          <span className="text-sm">
                            {person.reliability !== undefined &&
                              person.reliability !== null
                              ? `${Math.round(person.reliability)}%`
                              : "—"}
                          </span>
                        </td>
                        <td className="py-2 px-1 break-words text-sm">
                          <span className="text-sm">
                            {person.utilization !== undefined &&
                              person.utilization !== null
                              ? `${Math.round(person.utilization)}%`
                              : "—"}
                          </span>
                        </td>
                        <td className="py-2 px-1 break-words text-sm">
                          <div className="flex flex-col">
                            <span
                              className={`text-sm font-medium ${
                                String(person.status || "")
                                  .trim()
                                  .toLowerCase() === "active"
                                  ? "text-status-active"
                                  : "text-status-pending"
                              }`}
                            >
                              {formatPersonnelStatus(person.status)}
                            </span>
                            <span className="text-xs text-muted-foreground break-words">
                              {person.lastContact}
                            </span>
                          </div>
                        </td>
                        <td className="py-2 px-3 break-words">
                          {formatAccountDateTime(person.created, {
                            emptyValue: "—",
                          })}
                        </td>
                        {canWriteCurrentTab ? (
                        <td className="py-1 px-1">
                          <ActionDropdown
                            actions={person.status?.toLowerCase() === 'deleted' ? [
                              {
                                label: "Restore",
                                onClick: async () => {
                                  try {
                                    const ok = await showConfirm(
                                      "Are you sure you want to restore this account?"
                                    );
                                    if (!ok) return;
                                    const res = await fetch(
                                      `${BASE_URL}/api/personnel/accounts/${person.id}`,
                                      {
                                        method: "PUT",
                                        headers: {
                                          "Content-Type": "application/json",
                                          ...getAuthHeaders(),
                                        },
                                        body: JSON.stringify({ state: "active" }),
                                      }
                                    );
                                    const data = await res
                                      .json()
                                      .catch(() => ({}));
                                    if (!res.ok)
                                      throw new Error(
                                        data.message || "Activation failed"
                                      );
                                    showSuccess("Account restored successfully");
                                    // Remove from view if we are only showing deleted
                                    setPersonnelData((prev) =>
                                      prev.filter((p) => p.id !== person.id)
                                    );
                                  } catch (err) {
                                    console.error(err);
                                    showError(err.message || "Server error");
                                  }
                                },
                              }
                            ] : [
                              {
                                label: "Re-index Face",
                                onClick: async () => {
                                  try {
                                    await requestReindexFace(person.id);
                                    showSuccess("Face re-indexed successfully.");
                                  } catch (err) {
                                    console.error(err);
                                    showError(
                                      err.message || "Failed to re-index face",
                                      "Re-index Face",
                                    );
                                  }
                                },
                              },
                              {
                                label: "Delete",
                                onClick: async () => {
                                  try {
                                    const ok = await showConfirm(
                                      "Are you sure you want to delete this account?"
                                    );
                                    if (!ok) return;
                                    const res = await fetch(
                                      `${BASE_URL}/api/personnel/accounts/${person.id}`,
                                      {
                                        method: "DELETE",
                                        headers: getAuthHeaders(),
                                      }
                                    );
                                    const data = await res
                                      .json()
                                      .catch(() => ({}));
                                    if (!res.ok)
                                      throw new Error(
                                        data.message || "Delete failed"
                                      );
                                    showSuccess("Deleted");
                                    setPersonnelData((prev) =>
                                      prev.filter((p) => p.id !== person.id)
                                    );
                                  } catch (err) {
                                    console.error(err);
                                    showError(err.message || "Server error");
                                  }
                                },
                                danger: true,
                              },
                            ]}
                          />
                        </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {selectedTab === "CHANGE REQUESTS" && (
                <table className="w-full min-w-[1100px] table-fixed border-collapse">
                  <thead className="bg-[#dc1f0080] text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[8%]">ID</th>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[16%]">Temp Staff</th>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[16%]">Requested By</th>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[18%]">Requested At</th>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[10%]">Status</th>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[20%]">Summary</th>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[12%]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersonnel.map((request) => {
                      const changes = request?.requested_changes_json || {};
                      const changeCount = Object.keys(changes).length;
                      return (
                        <tr
                          key={request.request_id}
                          className="border-t border-border hover:bg-table-row-hover align-top"
                        >
                          <td className="py-3 px-3 text-sm text-muted-foreground">#{request.request_id}</td>
                          <td className="py-3 px-3 text-sm font-medium text-red-600">
                            {request.target_name?.trim() || "Unknown"}
                          </td>
                          <td className="py-3 px-3 text-sm">
                            <div>{request.requester_name?.trim() || "Unknown"}</div>
                            <div className="text-xs text-muted-foreground">{request.requester_email || ""}</div>
                          </td>
                          <td className="py-3 px-3 text-sm">
                            {formatAccountDateTime(request.created_at, {
                              emptyValue: "-",
                            })}
                          </td>
                          <td className="py-3 px-3 text-sm">
                            <span className={`font-medium ${request.status === "approved"
                              ? "text-status-active"
                              : request.status === "rejected"
                                ? "text-red-600"
                                : "text-status-pending"
                              }`}>
                              {request.status || "pending"}
                            </span>
                          </td>
                          <td className="py-3 px-3 text-sm">
                            {changeCount} field{changeCount === 1 ? "" : "s"} changed
                          </td>
                          <td className="py-3 px-3 text-sm">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => openChangeRequestDetail(request)}
                                className="px-2 py-1 border rounded text-xs hover:bg-gray-50"
                              >
                                View
                              </button>
                              {request.status === "pending" && (
                                <>
                                  <button
                                    onClick={() => approveChangeRequest(request.request_id)}
                                    disabled={requestActionLoadingId === request.request_id}
                                    className="px-2 py-1 bg-green-600 text-white rounded text-xs disabled:opacity-50"
                                  >
                                    Approve
                                  </button>
                                  <button
                                    onClick={() => rejectChangeRequest(request.request_id)}
                                    disabled={requestActionLoadingId === request.request_id}
                                    className="px-2 py-1 bg-red-600 text-white rounded text-xs disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}

              {selectedTab === "OFFICE STAFF" && (
                <table className="w-full min-w-[1100px] table-fixed border-collapse">
                  <thead className="bg-[#dc1f0080] text-white sticky top-0 z-10">
                    <tr>
                      <th className="py-2 px-1 text-left text-sm font-medium w-[17%] break-words-all">
  Name
</th>
                      <th className="py-2 px-3 text-left text-sm font-medium whitespace-nowrap w-[18ch] md:w-[20ch]">
                        Phone
                      </th>
                      <th className="py-2 px-3 text-left text-sm font-medium w-[28ch] md:w-[40ch]">
                        Roles
                      </th>
                      <th className="py-2 px-3 text-left text-sm font-medium">
                        Status
                      </th>
                      <th className="py-2 px-3 text-left text-sm font-medium">
                        Created <SortArrow colKey="created" />
                      </th>
                      {canWriteCurrentTab ? (
                        <th className="py-2 px-2 w-12"></th>
                      ) : null}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPersonnel.map((person) => (
                      <tr
                        key={person.id}
                        className="border-t border-border hover:bg-table-row-hover align-top"
                      >
                        <td
                          className={`py-4 px-3 ${person.status?.toLowerCase() === 'deleted' ? '' : 'cursor-pointer'}`}
                          onClick={() => {
                            if (person.status?.toLowerCase() === 'deleted') return;
                            setSelectedPerson(person);
                            setShowDetail(true);
                          }}
                        >
                          <div className="flex items-center space-x-3">
                            <AvatarComponent person={person} />
                            <div className={`min-w-0 max-w-[18ch] break-words whitespace-normal font-medium text-sm ${person.status?.toLowerCase() === 'deleted' ? 'text-gray-500' : 'text-red-600'}`}>
                              {person.name}
                            </div>
                          </div>
                        </td>
                        
                        
                        <td className="py-4 px-3 text-sm whitespace-nowrap">
                          <div className="min-w-0">{person.phone}</div>
                        </td>
                        <td className="py-4 px-3 text-sm">
                          <div className="min-w-0 truncate">
                            <RolesCell roles={person.roles} aclRoles={person.aclRoles} />
                          </div>
                        </td>
                        <td className="py-4 px-3 text-sm">
                          <div className="min-w-0 truncate">
                            {formatPersonnelStatus(person.status)}
                          </div>
                        </td>
                        <td className="py-4 px-3 text-sm">
                          <div className="min-w-0 truncate">
                            {formatAccountDateTime(person.created, {
                              emptyValue: "—",
                            })}
                          </div>
                        </td>
                        {canWriteCurrentTab ? (
                        <td className="py-1 px-2 text-right w-12">
                          <ActionDropdown
                            actions={person.status?.toLowerCase() === 'deleted' ? [
                              {
                                label: "Restore",
                                onClick: async () => {
                                  try {
                                    const ok = await showConfirm(
                                      "Are you sure you want to restore this account?"
                                    );
                                    if (!ok) return;
                                    const res = await fetch(
                                      `${BASE_URL}/api/personnel/accounts/${person.id}`,
                                      {
                                        method: "PUT",
                                        headers: {
                                          "Content-Type": "application/json",
                                          ...getAuthHeaders(),
                                        },
                                        body: JSON.stringify({ state: "active" }),
                                      }
                                    );
                                    const data = await res
                                      .json()
                                      .catch(() => ({}));
                                    if (!res.ok)
                                      throw new Error(
                                        data.message || "Activation failed"
                                      );
                                    showSuccess("Account restored successfully");
                                    // Remove from view if we are only showing deleted
                                    setPersonnelData((prev) =>
                                      prev.filter((p) => p.id !== person.id)
                                    );
                                  } catch (err) {
                                    console.error(err);
                                    showError(err.message || "Server error");
                                  }
                                },
                              }
                            ] : [
                              {
                                label: "Re-index Face",
                                onClick: async () => {
                                  try {
                                    await requestReindexFace(person.id);
                                    showSuccess("Face re-indexed successfully.");
                                  } catch (err) {
                                    console.error(err);
                                    showError(
                                      err.message || "Failed to re-index face",
                                      "Re-index Face",
                                    );
                                  }
                                },
                              },
                              {
                                label: "Delete",
                                onClick: async () => {
                                  try {
                                    const ok = await showConfirm(
                                      "Are you sure you want to delete this account?"
                                    );
                                    if (!ok) return;
                                    const res = await fetch(
                                      `${BASE_URL}/api/personnel/accounts/${person.id}`,
                                      {
                                        method: "DELETE",
                                        headers: getAuthHeaders(),
                                      }
                                    );
                                    const data = await res
                                      .json()
                                      .catch(() => ({}));
                                    if (!res.ok)
                                      throw new Error(
                                        data.message || "Delete failed"
                                      );
                                    showSuccess("Deleted");
                                    setPersonnelData((prev) =>
                                      prev.filter((p) => p.id !== person.id)
                                    );
                                  } catch (err) {
                                    console.error(err);
                                    showError(err.message || "Server error");
                                  }
                                },
                                danger: true,
                              },
                            ]}
                          />
                        </td>
                        ) : null}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Pagination: numbered, compact with ellipses, right-aligned */}
          <div className="flex items-center justify-center sm:justify-end mt-4 w-full">
            <nav
              className="flex flex-wrap items-center justify-center sm:justify-end mt-auto text-sm text-muted-foreground gap-2 w-full"
              aria-label="Pagination"
            >
              <button
                onClick={() => patchQuery({ currentPage: Math.max(currentPage - 1, 1) })}
                disabled={currentPage === 1}
                className="px-2.5 py-1.5 border border-[#DC1F0033] text-buttonWhite rounded-md hover:bg-gray-100 disabled:opacity-50"
              >
                Previous
              </button>

              {getPageList(totalPages || 1, currentPage).map((p, idx) =>
                typeof p === "string" ? (
                  <span key={`dots-${idx}`} className="px-2.5 py-1.5">
                    …
                  </span>
                ) : (
                  <button
                    key={p}
                    onClick={() => patchQuery({ currentPage: p })}
                    aria-current={p === currentPage ? "page" : undefined}
                    className={`px-2.5 py-1.5 border border-[#DC1F0033] rounded-md ${p === currentPage
                      ? "bg-[#DC1F00] text-white"
                      : "hover:bg-gray-100"
                      }`}
                  >
                    {p}
                  </button>
                )
              )}

              <button
                onClick={() => patchQuery({ currentPage: Math.min(currentPage + 1, totalPages || 1) })}
                disabled={currentPage === totalPages}
                className="px-2.5 py-1.5 bg-buttonRed hover:bg-buttonRed-hover text-white rounded-md disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
          </div>

          {!isChangeRequestsTab && (
            <AtlasFilterPanel
              isOpen={filterPanel.isOpen}
              onClose={filterPanel.close}
              onClear={clearAllFilters}
              activeCount={activeFilterCount}
            >
              <PersonnelFilterFields
                selectedTab={selectedTab}
                filters={filters}
                setFilters={patchFilters}
                focusedFieldId={filterPanel.focusedFieldId}
                onFocusClear={filterPanel.clearFocus}
                formatType={formatType}
                divisions={divisions}
                recruiters={recruiters}
                certifications={certifications}
                skills={skills}
                payers={payers}
                officeRoleOptions={officeRoleOptions}
              />
            </AtlasFilterPanel>
          )}
        </div>
      </div>
      {showCreateTemp && (
        <CreateTempModal
          onClose={() => setShowCreateTemp(false)}
          divisions={divisions}
          recruiters={recruiters}
          BASE_URL={BASE_URL}
          onSuccess={(created) => {
            setShowCreateTemp(false);
            patchQuery({ currentPage: 1 });
            if (created) {
              prependCreatedPerson(created);
              setSelectedPerson(created);
              setShowDetail(true);
            }
          }}
        />
      )}
      {showCreateOffice && (
        <CreateOfficeModal
          onClose={() => setShowCreateOffice(false)}
          divisions={divisions}
          BASE_URL={BASE_URL}
          onSuccess={(created) => {
            setShowCreateOffice(false);
            patchQuery({ currentPage: 1 });
            if (created) {
              prependCreatedPerson(created);
              setSelectedPerson(created);
              setShowDetail(true);
            }
          }}
        />
      )}
      {showDetail && selectedPerson && (
        <PersonnelDetailModal
          person={selectedPerson}
          type={selectedTab === "TEMP STAFF" ? "temp" : "office"}
          isOpen={showDetail}
          initialTab={initialDetailTab}
          readOnly={!canWriteCurrentTab}
          onUpdate={handlePersonUpdate}
          onClose={() => {
            setShowDetail(false);
            setSelectedPerson(null);
            setInitialDetailTab("profile");
          }}
        />
      )}
      {showRequestDetail && selectedRequest && (
        <div
          className="fixed inset-0 z-[120] bg-black/50 flex items-center justify-center p-4"
          onClick={() => {
            setShowRequestDetail(false);
            setSelectedRequest(null);
          }}
        >
          <div
            className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] overflow-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Temp Staff Change Request #{selectedRequest.request_id}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {selectedRequest.target_name || "Unknown"} |{" "}
                  {formatAccountDateTime(selectedRequest.created_at, {
                    emptyValue: "-",
                  })}
                </p>
              </div>
              <button
                onClick={() => {
                  setShowRequestDetail(false);
                  setSelectedRequest(null);
                }}
                className="text-gray-500 hover:text-gray-700 text-xl leading-none"
              >
                ×
              </button>
            </div>

            <div className="px-6 py-5 space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Status</div>
                  <div className="font-medium">{selectedRequest.status || "pending"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Requested By</div>
                  <div className="font-medium">{selectedRequest.requester_name || "Unknown"}</div>
                </div>
                <div>
                  <div className="text-xs uppercase text-muted-foreground">Reviewer</div>
                  <div className="font-medium">{selectedRequest.reviewer_name || "-"}</div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-2">Requested Field Changes</h4>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-3 py-2">Field</th>
                        <th className="text-left px-3 py-2">From</th>
                        <th className="text-left px-3 py-2">To</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(
                        selectedRequest.requested_changes_json &&
                          typeof selectedRequest.requested_changes_json === "object"
                          ? selectedRequest.requested_changes_json
                          : {}
                      ).map(([field, diff]) => {
                        const fromVal = diff && typeof diff === "object" && Object.prototype.hasOwnProperty.call(diff, "from")
                          ? diff.from
                          : "";
                        const toVal = diff && typeof diff === "object" && Object.prototype.hasOwnProperty.call(diff, "to")
                          ? diff.to
                          : diff;
                        return (
                          <tr key={field} className="border-t">
                            <td className="px-3 py-2 font-medium">{field}</td>
                            <td className="px-3 py-2 break-all">{fromVal == null || fromVal === "" ? "-" : String(fromVal)}</td>
                            <td className="px-3 py-2 break-all">{toVal == null || toVal === "" ? "-" : String(toVal)}</td>
                          </tr>
                        );
                      })}
                      {Object.keys(
                        selectedRequest.requested_changes_json &&
                          typeof selectedRequest.requested_changes_json === "object"
                          ? selectedRequest.requested_changes_json
                          : {}
                      ).length === 0 && (
                        <tr>
                          <td className="px-3 py-3 text-muted-foreground" colSpan={3}>
                            No field changes found in this request.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold mb-2">Attachments</h4>
                <div className="space-y-2">
                  {(Array.isArray(selectedRequest.attachments_json) ? selectedRequest.attachments_json : []).map((attachment, idx) => {
                    const downloadUrl = `${BASE_URL}/api/temp-staff/requests/${selectedRequest.request_id}/attachments/${idx}/download`;
                    const name = typeof attachment === "string"
                      ? (attachment.split("/").pop() || `attachment-${idx + 1}`)
                      : (attachment?.name || attachment?.file_name || `attachment-${idx + 1}`);
                    return (
                      <div key={`${name}-${idx}`} className="flex items-center justify-between border rounded px-3 py-2 text-sm">
                        <span className="truncate pr-3">{name}</span>
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                const response = await fetch(downloadUrl, {
                                  headers: getAuthHeaders(),
                                  credentials: "include",
                                });
                                if (!response.ok) {
                                  const error = await response
                                    .json()
                                    .catch(() => ({ message: "Failed to download attachment" }));
                                  throw new Error(error.message || "Failed to download attachment");
                                }
                                const blob = await response.blob();
                                const objectUrl = window.URL.createObjectURL(blob);
                                const link = document.createElement("a");
                                link.href = objectUrl;
                                link.download = name;
                                document.body.appendChild(link);
                                link.click();
                                document.body.removeChild(link);
                                window.URL.revokeObjectURL(objectUrl);
                              } catch (error) {
                                console.error("Attachment download failed", error);
                                showError(error.message || "Failed to download attachment");
                              }
                            }}
                            className="px-2 py-1 border rounded hover:bg-gray-50"
                          >
                            View
                          </button>
                        </div>
                      </div>
                    );
                  })}
                  {(!Array.isArray(selectedRequest.attachments_json) || selectedRequest.attachments_json.length === 0) && (
                    <p className="text-sm text-muted-foreground">No attachments.</p>
                  )}
                </div>
              </div>

              {selectedRequest.review_note && (
                <div>
                  <h4 className="text-sm font-semibold mb-1">Review Note</h4>
                  <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedRequest.review_note}</p>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t flex items-center justify-end gap-2">
              <button
                onClick={() => {
                  setShowRequestDetail(false);
                  setSelectedRequest(null);
                }}
                className="px-3 py-2 border rounded hover:bg-gray-50"
              >
                Close
              </button>
              {selectedRequest.status === "pending" && (
                <>
                  <button
                    onClick={() => rejectChangeRequest(selectedRequest.request_id)}
                    disabled={requestActionLoadingId === selectedRequest.request_id}
                    className="px-3 py-2 bg-red-600 text-white rounded disabled:opacity-50"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => approveChangeRequest(selectedRequest.request_id)}
                    disabled={requestActionLoadingId === selectedRequest.request_id}
                    className="px-3 py-2 bg-green-600 text-white rounded disabled:opacity-50"
                  >
                    Approve and Apply
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Personnel;

function CreateTempModal({
  onClose,
  divisions,
  recruiters,
  BASE_URL,
  onSuccess,
}) {
  const [form, setForm] = useState({ type: "candidate" });
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [showRecruiterDropdown, setShowRecruiterDropdown] = useState(false);
  const [payers, setPayers] = useState([]);
  const [payoutStaff, setPayoutStaff] = useState([]);
  const [employmentOfficeOptions, setEmploymentOfficeOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [phoneError, setPhoneError] = useState("");
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  // Helper to get auth headers
  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  // Debounced check for duplicate mobile phone
  useEffect(() => {
    const rawDigits = String(form.mobile || "").replace(/\D/g, "");
    if (!form.mobile || rawDigits.length < 10) {
      setPhoneError("");
      return;
    }

    let isCancelled = false;
    const timer = setTimeout(async () => {
      setIsCheckingPhone(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/personnel/check-phone?phone=${encodeURIComponent(form.mobile)}`,
          {
            credentials: "include",
            headers: getAuthHeaders(),
          }
        );
        const data = await res.json();
        if (!isCancelled) {
          if (data && data.isDuplicate) {
            setPhoneError("This phone number already exists in the system");
          } else {
            setPhoneError("");
          }
        }
      } catch (err) {
        console.error("Phone duplicate check error:", err);
      } finally {
        if (!isCancelled) setIsCheckingPhone(false);
      }
    }, 350);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [form.mobile, BASE_URL]);

  // Load payout staff on-demand
  const loadPayoutStaff = async () => {
    if (payoutStaff.length > 0) return; // Already loaded
    try {
      let res = await fetch(`${BASE_URL}/api/admin/accounts?limit=1000`, { credentials: 'include' });
      if (!res.ok) {
        // Retry with Authorization header if cookie-based auth fails
        res = await fetch(`${BASE_URL}/api/admin/accounts?limit=1000`, { headers: getAuthHeaders() });
      }
      const json = await res.json().catch(() => null);
      const list = Array.isArray(json) ? json : (json && Array.isArray(json.data) ? json.data : []);
      setPayoutStaff(list || []);
    } catch (e) {
      console.error('Payout staff load failed', e);
      setPayoutStaff([]);
    }
  };

  useEffect(() => {
    // Load Payers on mount
    fetch(`${BASE_URL}/api/personnel/payers`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => setPayers(Array.isArray(j) ? j : (j.data || [])))
      .catch(console.error);

    // Load employment offices from the same source used by profile dropdowns.
    // Keep divisions as fallback when offices endpoint is unavailable.
    fetch(`${BASE_URL}/api/personnel/offices`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => setEmploymentOfficeOptions(Array.isArray(j) ? j : (j.data || [])))
      .catch(() => setEmploymentOfficeOptions([]));
  }, []);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  // Candidate / Field Staff start with the "Cheque - 44hrs" payer once payers have loaded
  useEffect(() => {
    if (!usesDefaultPayer(form.type) || form.payrollPayerId) return;
    const defaultPayerId = findDefaultPayerId(payers);
    if (defaultPayerId) setField("payrollPayerId", defaultPayerId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payers]);

  const requiresPermitUpload = [
    "study_resident",
    "temporary_resident",
    "other",
  ].includes(form.residencyStatus);

  // Required fields vary by staff Type; firstName/lastName/employmentOfficeId are always
  // required across all types, and email/mobile for every type except Outsourced Staff.
  const requiredFieldsByType = {
    candidate: [],
    fieldstaff: ["address", "emergencyName", "emergencyPhone", "payrollPayerId"],
    outsourcedstaff: ["payrollPayerId"],
  };
  const typeRequiredFields =
    requiredFieldsByType[form.type] || requiredFieldsByType.candidate;
  const isRequired = (field) => typeRequiredFields.includes(field);
  const contactRequired = form.type !== "outsourcedstaff";
  const payerFieldLabel = form.type === "outsourcedstaff" ? "Agency" : "Payroll Payer";

  // Drag Events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const uploaded = e.dataTransfer.files[0];
      const validationMessage = validateStaffDocumentFile(uploaded);
      if (validationMessage) return showError(validationMessage);
      setSelectedFile(uploaded);
      // Do NOT attach the File object into the JSON form payload
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const uploaded = e.target.files[0];
      const validationMessage = validateStaffDocumentFile(uploaded);
      if (validationMessage) {
        showError(validationMessage);
        e.target.value = "";
        return;
      }
      setSelectedFile(uploaded);
      // Do NOT attach the File object into the JSON form payload
    }
  };

  const getResidencyTitle = () => {
    switch (form.residencyStatus) {
      case "study_resident":
        return "Study Permit";
      case "temporary_resident":
        return "Work Permit";
      case "other":
        return "Refugee document";
      default:
        return "";
    }
  };

  const handleViewSelectedFile = () => {
    if (!selectedFile) return;
    const url = URL.createObjectURL(selectedFile);
    window.open(url, "_blank", "noopener,noreferrer");
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const handleDownloadSelectedFile = () => {
    if (!selectedFile) return;
    const url = URL.createObjectURL(selectedFile);
    const link = document.createElement("a");
    link.href = url;
    link.download = selectedFile.name || "residency-document";
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;
    if (!form.firstName || !form.lastName)
      return showError(contactRequired ? "First, Last, Email are required" : "First, Last are required");
    if (contactRequired && !form.email)
      return showError("First, Last, Email are required");
    if (form.email && !isValidEmail(form.email))
      return showError("Please enter a valid email address");
    if (form.mobile && !isValidPhoneNumber(form.mobile))
      return showError("Invalid mobile number");
    if (form.emergencyPhone && !isValidPhoneNumber(form.emergencyPhone))
      return showError("Invalid emergency number");
    if (form.personalEmail && !isValidEmail(form.personalEmail))
      return showError("Please enter a valid personal email");
    if (contactRequired && (!form.mobile || form.mobile.length < 10)) {
      return showError("Please enter a valid Mobile number on record with Talent.");
    }
    if (!form.employmentOfficeId) {
      return showError("Please select an Employment Office.");
    }
    if (isRequired("address") && !(form.addressRaw || form.address)) {
      return showError("Please enter an Address.");
    }
    if (isRequired("emergencyName") && !form.emergencyName) {
      return showError("Please enter an Emergency Contact Name.");
    }
    if (isRequired("emergencyPhone") && !form.emergencyPhone) {
      return showError("Please enter an Emergency Contact Number.");
    }
    if (isRequired("payrollPayerId") && !form.payrollPayerId) {
      return showError(`Please select a ${payerFieldLabel}.`);
    }
    if (isRequired("hstNumber") && !form.hstNumber) {
      return showError("Please enter an HST Number.");
    }
    if (phoneError) {
      return showError("This phone number already exists in the system");
    }
    if (requiresPermitUpload && !selectedFile) {
      return showError(
        `Please upload the required ${getResidencyTitle()} before saving.`
      );
    }
    if (form.mobile) {
      try {
        const checkRes = await fetch(
          `${BASE_URL}/api/personnel/check-phone?phone=${encodeURIComponent(form.mobile)}`,
          {
            credentials: "include",
            headers: getAuthHeaders(),
          }
        );
        const checkData = await checkRes.json();
        if (checkData && checkData.isDuplicate) {
          setPhoneError("This phone number already exists in the system");
          return showError("This phone number already exists in the system");
        }
      } catch (e) {
        console.warn("Pre-submit phone check failed:", e);
      }
    }
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      // Send only serializable fields (do not include File objects)
      const payload = {
        type: form.type,
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        mobile: form.mobile,
        address: form.address,
        emergencyName: form.emergencyName,
        emergencyPhone: form.emergencyPhone,
        personalNumber: form.personalNumber,
        personalEmail: form.personalEmail,
        employmentOfficeId: form.employmentOfficeId,
        recruiterAccountId: form.recruiterAccountId,
        residencyStatus: form.residencyStatus,
        payrollPayerId: form.payrollPayerId,
        payoutStaffAccountId: form.payoutStaffAccountId,
        sin: form.sin,
        hstNumber: form.hstNumber,
        // New optional create fields (aligned with backend canonical keys)
        middle_name: form.middleName || null,
        preferred_name: form.preferredName || null,
        gender: form.gender || null,
        date_of_birth: form.dateOfBirth || null,
        line1: form.line1 || null,
        line2: form.line2 || null,
        city: form.city || null,
        province: form.province || null,
        postal_code: form.postal_code || null,
        country: form.country || null,
      };

      const res = await fetch(`${BASE_URL}/api/personnel/temp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message && data.message.includes("already exists")) {
          setPhoneError(data.message);
        }
        throw new Error(data.message || "Failed");
      }

      // If a file was selected, upload it via multipart.
      // For residency permit files, use the registration `upload-and-link` endpoint
      // so the server links it as the residency document (so the detail modal shows it).
      if (selectedFile && data && data.accountId) {
        try {
          // If the residencyStatus indicates a permit, call upload-and-link
          const serverResidency = form.residencyStatus; // values like 'study_resident' or 'temporary_resident'
          const mapToDocType = (v) => {
            if (!v) return null;
            switch (String(v)) {
              case 'study_resident':
                return 'study_permit';
              case 'temporary_resident':
                return 'work_permit';
              case 'other':
                return 'refugee';
              default:
                return null;
            }
          };

          const docType = mapToDocType(serverResidency);
          if (docType) {
            const fd = new FormData();
            fd.append('file', selectedFile);
            fd.append('docType', docType);

            const linkRes = await fetch(`${BASE_URL}/api/registration/${data.accountId}/upload-and-link`, {
              method: 'POST',
              credentials: 'include',
              body: fd,
            });
            const linkJson = await linkRes.json().catch(() => ({}));
            if (!linkRes.ok || !linkJson.file_id) {
              console.warn('upload-and-link failed, falling back to generic file upload', linkJson);
              // fallback to generic file upload
            } else {
              // success — we linked the residency file
              //console.log('upload-and-link succeeded', linkJson);
              showSuccess('Temp staff created and permit uploaded');
              if (onSuccess) onSuccess(data.created || null);
              return;
            }
          }

          // Fallback: generic files upload (if not a residency doc or upload-and-link failed)
          let typeId = null;
          try {
            const ftRes = await fetch(`${BASE_URL}/api/personnel/file-types`, { credentials: 'include' });
            const ftJson = await ftRes.json().catch(() => ({}));
            const types = Array.isArray(ftJson.data) ? ftJson.data : [];
            const pick = types.find(t => /permit|work|visa/i.test(t.name)) || types[0];
            typeId = pick ? (pick.type_id || pick.id) : null;
          } catch (e) {
            console.warn('Failed to load file types for auto-upload', e);
          }

          const formData = new FormData();
          formData.append('file', selectedFile);
          formData.append('state', 'active');
          if (typeId) formData.append('type_id', typeId);
          formData.append('note', getResidencyTitle() || 'Permit document');

          const upRes = await fetch(`${BASE_URL}/api/personnel/accounts/${data.accountId}/files`, {
            method: 'POST',
            credentials: 'include',
            body: formData,
          });
          const upJson = await upRes.json().catch(() => ({}));
          if (!upRes.ok || !upJson.success) {
            console.warn('Permit upload failed:', upJson.message || upJson);
            showError('Temp staff created but permit upload failed');
          }
        } catch (uploadErr) {
          console.warn('Permit upload error', uploadErr);
          showError('Temp staff created but permit upload failed');
        }
      }

      showSuccess('Temp staff created');
      if (onSuccess) onSuccess(data.created || null);
    } catch (err) {
      showError(err.message || 'Server error');
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-xl max-h-[90vh] p-6 relative flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          ✖
        </button>
        <h2 className="text-xl font-semibold mb-4">Create Temp Staff</h2>
        <div className="flex-1 overflow-y-auto pr-1">
          <form onSubmit={submit} className="flex flex-col gap-3" autoComplete="off">
            <div>
              {/* <div>
              <label className="block text-sm font-medium">Candidate</label>
              <input className="w-full border rounded px-3 py-2" value={form.candidate || ''} onChange={e => setField('candidate', e.target.value)} />
            </div> */}
              <div>
                <label className="block text-sm font-medium">Type*</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.type}
                  onChange={(e) => {
                    const type = e.target.value;
                    setForm((prev) => ({
                      ...prev,
                      type,
                      payrollPayerId: payerForTypeChange(type, prev.payrollPayerId, payers),
                    }));
                  }}
                >
                  <option value="candidate">Candidate</option>
                  <option value="fieldstaff">Field Staff</option>
                  <option value="outsourcedstaff">Outsourced Staff</option>
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">First Name*</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.firstName || ""}
                  onChange={(e) => setField("firstName", e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Last Name*</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.lastName || ""}
                  onChange={(e) => setField("lastName", e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">Middle Name</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.middleName || ""}
                  onChange={(e) => setField("middleName", e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Preferred Name</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.preferredName || ""}
                  onChange={(e) => setField("preferredName", e.target.value)}
                  autoComplete="off"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">Email{contactRequired && "*"}</label>
                <input
                  type="email"
                  className="w-full border rounded px-3 py-2"
                  value={form.email || ""}
                  onChange={(e) => setField("email", e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Gender</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.gender || ""}
                  onChange={(e) => setField("gender", e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">Date of Birth</label>
                <DateInput
                  className="w-full border rounded px-3 py-2"
                  value={form.dateOfBirth || ""}
                  onChange={(value) => setField("dateOfBirth", value)}
                  min="1900-01-01"
                />
              </div>
              <div />
            </div>
            {/* Contact Information Section */}
            <div className="mt-4 mb-2">
              <h3 className="text-base font-semibold text-gray-700 border-b pb-2">Contact Information</h3>
            </div>
            <div>
              <div>
                <label className="block text-sm font-medium">
                  Mobile number on record with Talent{contactRequired && "*"}
                </label>
                <PhoneInput
                  value={form.mobile || ""}
                  onChange={(v) => {
                    setField("mobile", v);
                    if (phoneError) setPhoneError("");
                  }}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  className={`w-full mb-1 border rounded px-3 py-2 ${
                    phoneError ? "border-red-500 ring-1 ring-red-500" : ""
                  }`}
                />
                {phoneError && (
                  <p className="text-red-600 text-xs font-medium mb-2">{phoneError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">Address{isRequired("address") ? "*" : ""}</label>
                <AddressAutocomplete
                  value={form.addressRaw || form.address || ""}
                  onChange={(v) => applyAddressToForm(setField, v)}
                />
              </div>
            </div>
            <div>
              <div>
                <label className="block text-sm font-medium">
                  Emergency Contact Name{isRequired("emergencyName") ? "*" : ""}
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.emergencyName || ""}
                  onChange={(e) => setField("emergencyName", e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Emergency Contact Number{isRequired("emergencyPhone") ? "*" : ""}
                </label>
                <PhoneInput
                  value={form.emergencyPhone || ""}
                  onChange={(v) => setField("emergencyPhone", v)}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  className="w-full border rounded px-3 py-2"
                />
              </div>
            </div>
            {/* Employment Information Section */}
            <div className="mt-4 mb-2">
              <h3 className="text-base font-semibold text-gray-700 border-b pb-2">Employment Information</h3>
            </div>
            <div>
              <div>
                <label className="block text-sm font-medium">
                  Employment Office*
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.employmentOfficeId || ""}
                  onChange={(e) =>
                    setField("employmentOfficeId", e.target.value)
                  }
                >
                  <option value="">Select</option>
                  {(employmentOfficeOptions.length > 0 ? employmentOfficeOptions : divisions).map((o) => (
                    <option
                      key={o.office_id || o.id || o.division_id}
                      value={o.office_id || o.id || o.division_id}
                    >
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">Recruiter</label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.recruiterAccountId || ""}
                  onChange={(e) =>
                    setField("recruiterAccountId", e.target.value)
                  }
                >
                  <option value="">Select</option>
                  {recruiters.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.name || `${r.first_name || ''} ${r.last_name || ''}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Payroll Information Section */}
            <div className="mt-4 mb-2">
              <h3 className="text-base font-semibold text-gray-700 border-b pb-2">Payroll Information</h3>
            </div>
            <div>
              <div>
                <label className="block text-sm font-medium">
                  Residency Status
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.residencyStatus || ""}
                  onChange={(e) => setField("residencyStatus", e.target.value)}
                >
                  <option value="">Select</option>
                  <option value="citizen">
                    Canadian Citizen / Permanent Resident
                  </option>
                  <option value="study_resident">Study permit</option>
                  <option value="temporary_resident">Work permit</option>
                  <option value="other">Refugee</option>
                </select>
                {/* Permit Upload Section */}
                {requiresPermitUpload && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">
                      {getResidencyTitle()}*
                    </label>

                    {/* If file uploaded → show file name */}
                    {selectedFile ? (
                      <div className="flex items-center justify-between bg-gray-100 border rounded p-3">
                        <div className="min-w-0">
                          <span className="text-sm text-gray-700 break-words">
                            {selectedFile.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-3 ml-3">
                          <button
                            type="button"
                            className="text-sm text-blue-600 hover:underline"
                            onClick={handleViewSelectedFile}
                          >
                            View
                          </button>
                          <button
                            type="button"
                            className="text-sm text-blue-600 hover:underline"
                            onClick={handleDownloadSelectedFile}
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            className="text-sm text-[#DC1F00] hover:underline"
                            onClick={() => {
                              setSelectedFile(null);
                              setField("permitDocument", null);
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      </div>
                    ) : (
                      /* Drag and Drop Area */
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`border-2 border-dashed rounded p-6 text-center ${dragActive
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300"
                          }`}
                      >
                        <p className="text-sm text-gray-600">
                          Drag & Drop your file
                        </p>
                        <p className="text-xs text-gray-400">or</p>

                        <label className="mt-2 inline-block bg-buttonRed text-white px-4 py-2 rounded cursor-pointer">
                          Upload
                          <input
                            type="file"
                            accept={STAFF_DOCUMENT_ACCEPT}
                            className="hidden"
                            onChange={handleFileSelect}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">
                  {payerFieldLabel}{isRequired("payrollPayerId") ? "*" : ""}
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.payrollPayerId || ""}
                  onChange={(e) => setField("payrollPayerId", e.target.value)}
                >
                  <option value="">{`Select ${payerFieldLabel}`}</option>
                  {payers.map((p) => (
                    <option key={p.payer_id || p.id} value={p.payer_id || p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Payout Staff
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.payoutStaffAccountId || ""}
                  onChange={(e) => setField("payoutStaffAccountId", e.target.value)}
                  onFocus={loadPayoutStaff}
                >
                  <option value="">Select Staff</option>
                  {payoutStaff.map((s) => (
                    <option key={s.account_id || s.id} value={s.account_id || s.id}>
                      {s.name || `${s.first_name || ''} ${s.last_name || ''}`.trim() || `Account #${s.account_id || s.id}`}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <div>
                <label className="block text-sm font-medium">
                  Social Insurance Number
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.sin || ""}
                  onChange={(e) => setField("sin", e.target.value)}
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">HST Number{isRequired("hstNumber") ? "*" : ""}</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.hstNumber || ""}
                  onChange={(e) => setField("hstNumber", e.target.value)}
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function CreateOfficeModal({ onClose, divisions, BASE_URL, onSuccess }) {
  const [form, setForm] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [employmentOfficeOptions, setEmploymentOfficeOptions] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitLockRef = useRef(false);
  const [phoneError, setPhoneError] = useState("");
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  const getAuthHeaders = () => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    if (token && token !== 'null' && token !== 'undefined' && token.trim() !== '') {
      return { 'Authorization': `Bearer ${token}` };
    }
    return {};
  };

  // Debounced check for duplicate mobile phone
  useEffect(() => {
    const rawDigits = String(form.mobile || "").replace(/\D/g, "");
    if (!form.mobile || rawDigits.length < 10) {
      setPhoneError("");
      return;
    }

    let isCancelled = false;
    const timer = setTimeout(async () => {
      setIsCheckingPhone(true);
      try {
        const res = await fetch(
          `${BASE_URL}/api/personnel/check-phone?phone=${encodeURIComponent(form.mobile)}`,
          {
            credentials: "include",
            headers: getAuthHeaders(),
          }
        );
        const data = await res.json();
        if (!isCancelled) {
          if (data && data.isDuplicate) {
            setPhoneError("This phone number already exists in the system");
          } else {
            setPhoneError("");
          }
        }
      } catch (err) {
        console.error("Phone duplicate check error:", err);
      } finally {
        if (!isCancelled) setIsCheckingPhone(false);
      }
    }, 350);

    return () => {
      isCancelled = true;
      clearTimeout(timer);
    };
  }, [form.mobile, BASE_URL]);

  const setField = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const requiresPermitUpload = [
    "study_resident",
    "temporary_resident",
    "other",
  ].includes(form.residencyStatus);

  useEffect(() => {
    fetch(`${BASE_URL}/api/personnel/offices`, { credentials: "include" })
      .then((r) => r.json())
      .then((j) =>
        setEmploymentOfficeOptions(Array.isArray(j) ? j : j.data || []),
      )
      .catch(() => setEmploymentOfficeOptions([]));
  }, [BASE_URL]);

  // Drag Events
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const uploaded = e.dataTransfer.files[0];
      const validationMessage = validateStaffDocumentFile(uploaded);
      if (validationMessage) return showError(validationMessage);
      setSelectedFile(uploaded);
    }
  };

  const handleFileSelect = (e) => {
    if (e.target.files && e.target.files[0]) {
      const uploaded = e.target.files[0];
      const validationMessage = validateStaffDocumentFile(uploaded);
      if (validationMessage) {
        showError(validationMessage);
        e.target.value = "";
        return;
      }
      setSelectedFile(uploaded);
    }
  };

  const getResidencyTitle = () => {
    switch (form.residencyStatus) {
      case "study_resident":
        return "Study Permit";
      case "temporary_resident":
        return "Work Permit";
      case "other":
        return "Refugee document";
      default:
        return "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current) return;
    if (
      !form.firstName ||
      !form.lastName ||
      !form.email ||
      !String(form.mobile || "").trim() ||
      !form.address ||
      !form.emergencyName ||
      !String(form.emergencyPhone || "").trim() ||
      !form.residencyStatus ||
      !form.sin ||
      !form.hstNumber
    )
      return showError("Please fill all required fields");
    if (!isValidEmail(form.email))
      return showError("Please enter a valid email address");
    if (form.mobile && !isValidPhoneNumber(form.mobile))
      return showError("Invalid mobile number");
    if (form.emergencyPhone && !isValidPhoneNumber(form.emergencyPhone))
      return showError("Invalid emergency number");
    if (form.personalEmail && !isValidEmail(form.personalEmail))
      return showError("Please enter a valid personal email");
    if (phoneError) {
      return showError("This phone number already exists in the system");
    }
    if (requiresPermitUpload && !selectedFile) {
      return showError(
        `Please upload the required ${getResidencyTitle()} before saving.`
      );
    }
    if (form.mobile) {
      try {
        const checkRes = await fetch(
          `${BASE_URL}/api/personnel/check-phone?phone=${encodeURIComponent(form.mobile)}`,
          {
            credentials: "include",
            headers: getAuthHeaders(),
          }
        );
        const checkData = await checkRes.json();
        if (checkData && checkData.isDuplicate) {
          setPhoneError("This phone number already exists in the system");
          return showError("This phone number already exists in the system");
        }
      } catch (e) {
        console.warn("Pre-submit phone check failed:", e);
      }
    }
    submitLockRef.current = true;
    setIsSubmitting(true);
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        mobile: form.mobile,
        address: form.address,
        emergencyName: form.emergencyName,
        emergencyPhone: form.emergencyPhone,
        personalNumber: form.personalNumber,
        personalEmail: form.personalEmail,
        employmentOfficeId: form.employmentOfficeId,
        residencyStatus: form.residencyStatus,
        sin: form.sin,
        hstNumber: form.hstNumber,
        line1: form.line1 || null,
        line2: form.line2 || null,
        city: form.city || null,
        province: form.province || null,
        postal_code: form.postal_code || null,
        country: form.country || null,
      };

      const res = await fetch(`${BASE_URL}/api/personnel/office`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.message && data.message.includes("already exists")) {
          setPhoneError(data.message);
        }
        throw new Error(data.message || "Failed");
      }

      const accountId =
        data?.accountId ||
        data?.account_id ||
        data?.id ||
        data?.created?.id ||
        data?.created?.account_id ||
        null;

      if (selectedFile && accountId) {
        try {
          const serverResidency = form.residencyStatus;
          const mapToDocType = (v) => {
            if (!v) return null;
            switch (String(v)) {
              case "study_resident":
                return "study_permit";
              case "temporary_resident":
                return "work_permit";
              case "other":
                return "refugee";
              default:
                return null;
            }
          };

          const docType = mapToDocType(serverResidency);
          if (docType) {
            const fd = new FormData();
            fd.append("file", selectedFile);
            fd.append("docType", docType);

            const linkRes = await fetch(
              `${BASE_URL}/api/registration/${accountId}/upload-and-link`,
              {
                method: "POST",
                credentials: "include",
                body: fd,
              },
            );
            const linkJson = await linkRes.json().catch(() => ({}));
            if (!linkRes.ok || !linkJson.file_id) {
              console.warn(
                "upload-and-link failed, falling back to generic file upload",
                linkJson,
              );
            } else {
              showSuccess("Office staff created and permit uploaded");
              if (onSuccess) onSuccess(data.created || null);
              return;
            }
          }

          let typeId = null;
          try {
            const ftRes = await fetch(`${BASE_URL}/api/personnel/file-types`, {
              credentials: "include",
            });
            const ftJson = await ftRes.json().catch(() => ({}));
            const types = Array.isArray(ftJson.data) ? ftJson.data : [];
            const pick =
              types.find((t) => /permit|work|visa/i.test(t.name)) || types[0];
            typeId = pick ? pick.type_id || pick.id : null;
          } catch (e) {
            console.warn("Failed to load file types for auto-upload", e);
          }

          const formData = new FormData();
          formData.append("file", selectedFile);
          formData.append("state", "active");
          if (typeId) formData.append("type_id", typeId);
          formData.append("note", getResidencyTitle() || "Permit document");

          const upRes = await fetch(
            `${BASE_URL}/api/personnel/accounts/${accountId}/files`,
            {
              method: "POST",
              credentials: "include",
              body: formData,
            },
          );
          const upJson = await upRes.json().catch(() => ({}));
          if (!upRes.ok || !upJson.success) {
            console.warn("Permit upload failed:", upJson.message || upJson);
            showError("Office staff created but permit upload failed");
          }
        } catch (uploadErr) {
          console.warn("Permit upload error", uploadErr);
          showError("Office staff created but permit upload failed");
        }
      }

      showSuccess("Office staff created");
      if (onSuccess) onSuccess(data.created || null);
    } catch (err) {
      showError(err.message || "Server error");
    } finally {
      submitLockRef.current = false;
      setIsSubmitting(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] p-6 relative flex flex-col">
        <button
          onClick={onClose}
          className="absolute top-3 right-3 text-gray-500 hover:text-gray-700"
        >
          ✖
        </button>
        <h2 className="text-xl font-semibold mb-4">Create Office Staff</h2>
        <div className="flex-1 overflow-y-auto pr-1">
          <form onSubmit={submit} className="flex flex-col gap-3" autoComplete="off">
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">First Name*</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.firstName || ""}
                  onChange={(e) => setField("firstName", e.target.value)}
                  required
                  autoComplete="off"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">Last Name*</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.lastName || ""}
                  onChange={(e) => setField("lastName", e.target.value)}
                  required
                  autoComplete="off"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium">Email*</label>
              <input
                type="email"
                className="w-full border rounded px-3 py-2"
                value={form.email || ""}
                onChange={(e) => setField("email", e.target.value)}
                required
                autoComplete="off"
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">
                  Mobile number on record with Talent*
                </label>
                <PhoneInput
                  value={form.mobile || ""}
                  onChange={(v) => {
                    setField("mobile", v);
                    if (phoneError) setPhoneError("");
                  }}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  className={`w-full ${
                    phoneError ? "border-red-500 ring-1 ring-red-500 rounded" : ""
                  }`}
                />
                {phoneError && (
                  <p className="text-red-600 text-xs font-medium mt-1">{phoneError}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium">Address*</label>
                <AddressAutocomplete
                  value={form.addressRaw || form.address || ""}
                  onChange={(v) => applyAddressToForm(setField, v)}
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">
                  Emergency Contact Name*
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.emergencyName || ""}
                  onChange={(e) => setField("emergencyName", e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Emergency Contact Number*
                </label>
                <PhoneInput
                  value={form.emergencyPhone || ""}
                  onChange={(v) => setField("emergencyPhone", v)}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  className="w-full"
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">
                  Personal Number
                </label>
                <PhoneInput
                  value={form.personalNumber || ""}
                  onChange={(v) => setField("personalNumber", v)}
                  defaultCountry="CA"
                  international
                  withCountryCallingCode
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Personal Email
                </label>
                <input
                  type="email"
                  className="w-full border rounded px-3 py-2"
                  value={form.personalEmail || ""}
                  onChange={(e) => setField("personalEmail", e.target.value)}
                />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">
                  Employment Office*
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.employmentOfficeId || ""}
                  required
                  onChange={(e) =>
                    setField("employmentOfficeId", e.target.value)
                  }
                >
                  <option value="">Select</option>
                  {(employmentOfficeOptions.length > 0
                    ? employmentOfficeOptions
                    : divisions
                  ).map((o) => (
                    <option
                      key={o.office_id || o.id || o.division_id}
                      value={o.office_id || o.id || o.division_id}
                    >
                      {o.name}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium">
                  Residency Status*
                </label>
                <select
                  className="w-full border rounded px-3 py-2"
                  value={form.residencyStatus || ""}
                  onChange={(e) => setField("residencyStatus", e.target.value)}
                  required
                >
                  <option value="">Select</option>
                  <option value="citizen">
                    Canadian Citizen / Permanent Resident
                  </option>
                  <option value="study_resident">Study permit</option>
                  <option value="temporary_resident">Work permit</option>
                  <option value="other">Refugee</option>
                </select>

                {/* Permit Upload Section */}
                {requiresPermitUpload && (
                  <div className="mt-3">
                    <label className="block text-sm font-medium mb-1">
                      {getResidencyTitle()}*
                    </label>

                    {/* If file uploaded → show file name */}
                    {selectedFile ? (
                      <div className="flex items-center justify-between bg-gray-100 border rounded p-3">
                        <span className="text-sm text-gray-700">
                          {selectedFile.name}
                        </span>

                        <button
                          type="button"
                          className="text-sm text-[#DC1F00] hover:underline"
                          onClick={() => {
                            setSelectedFile(null);
                            setField("permitDocument", null);
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      /* Drag and Drop Area */
                      <div
                        onDragEnter={handleDrag}
                        onDragLeave={handleDrag}
                        onDragOver={handleDrag}
                        onDrop={handleDrop}
                        className={`border-2 border-dashed rounded p-6 text-center ${dragActive
                          ? "border-blue-500 bg-blue-50"
                          : "border-gray-300"
                          }`}
                      >
                        <p className="text-sm text-gray-600">
                          Drag & Drop your file
                        </p>
                        <p className="text-xs text-gray-400">or</p>

                        <label className="mt-2 inline-block bg-buttonRed text-white px-4 py-2 rounded cursor-pointer">
                          Upload
                          <input
                            type="file"
                            accept={STAFF_DOCUMENT_ACCEPT}
                            className="hidden"
                            onChange={handleFileSelect}
                          />
                        </label>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium">
                  Social Insurance Number*
                </label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.sin || ""}
                  onChange={(e) => setField("sin", e.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium">HST Number*</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={form.hstNumber || ""}
                  onChange={(e) => setField("hstNumber", e.target.value)}
                  required
                />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-2">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-sidebar-active text-white rounded-lg hover:bg-red-600 disabled:opacity-50"
                disabled={isSubmitting}
              >
                {isSubmitting ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
